/**
 * Greek catalogue — the product's own language, not a translation of it.
 *
 * Keys mirror `en.js` exactly. A key present here and absent there is a key nothing falls back to,
 * so add to both or to neither.
 *
 * Two conventions worth keeping. Second person singular throughout ("η διαδρομή σου", not "σας"):
 * this is a colleague-to-colleague commuting app, and the plural form reads like a bank. And Greek
 * sentences are longer than their English source — roughly 10-20% — so anything living in a chip,
 * a button or a table header is worded tighter here rather than translated literally.
 */
export default {
  // ── Κοινά ─────────────────────────────────────────────────────────────────────────────────────
  "common.save": "Αποθήκευση",
  "common.cancel": "Ακύρωση",
  "common.edit": "Επεξεργασία",
  "common.add": "Προσθήκη",
  "common.close": "Κλείσιμο",
  "common.retry": "Δοκίμασε ξανά",
  "common.loading": "Φόρτωση…",
  "common.confirm": "Επιβεβαίωση",
  "common.neverMind": "Άκυρο",

  // Greek puts the symbol after the amount, with a space: 2,47 €. English puts it in front. The
  // number itself is already formatted by `Intl` before it arrives here — this decides placement.
  "common.money": "{amount} €",

  // ── Μονάδες ───────────────────────────────────────────────────────────────────────────────────
  // Every distance in the app goes through these two, so the abbreviation is decided in one place.
  // Greek writes χλμ, with a space before it — "20,9 χλμ", never "20.9km".
  "units.km": "{n} χλμ",
  "units.m": "{n} μ",
  "units.kg": "{n} κιλά",

  "offer.seatsOffered": "Προσφερόμενες θέσεις",
  "offer.seatWord": { one: "θέση", other: "θέσεις" },
  "offer.seatsAlreadyTaken": { one: "1 θέση είναι ήδη πιασμένη", other: "{n} θέσεις είναι ήδη πιασμένες" },

  // Chips in a fixed-width row, so short: "ΟΔΗΓΗΣΑ"/"ΤΑΞΙΔΕΨΑ" rather than a full phrase.
  "past.drove": "ΟΔΗΓΗΣΑ",
  "past.rode": "ΤΑΞΙΔΕΨΑ",
  // Non-breaking space: a narrow row wraps before «2 επιβάτες», never between the number and its word.
  "past.riders": { one: "1 επιβάτης", other: "{n} επιβάτες" },
  "past.withDriver": "με {name}",
  "past.driverWord": "οδηγό",
  "past.free": "δωρεάν",

  // ── Άδεια τοποθεσίας ──────────────────────────────────────────────────────────────────────────
  // The menu paths below are what the browser itself shows, and Chrome/Safari on a Greek phone show
  // them in Greek — so these are translated rather than left as English breadcrumbs.
  "geo.unavailable": "Η συσκευή σου δεν μπορεί να εντοπίσει τη θέση σου αυτή τη στιγμή — συνηθισμένο σε εσωτερικούς χώρους ή υπόγεια.",
  "geo.timeout": "Ο εντοπισμός της θέσης σου αργεί περισσότερο από το αναμενόμενο.",
  "geo.unsupported": "Αυτό το πρόγραμμα περιήγησης δεν μπορεί να μοιραστεί την τοποθεσία σου.",
  "geo.denied.orSearch": "Ή απλώς αναζήτησε τη διεύθυνση παρακάτω.",
  "geo.denied.insecure":
    "Αυτή η σελίδα δεν έχει ασφαλή σύνδεση, οπότε το πρόγραμμα περιήγησης δεν θα μοιραστεί την τοποθεσία σου ούτε θα ζητήσει άδεια.",
  "geo.denied.ios":
    "Η τοποθεσία είναι απενεργοποιημένη για αυτόν τον ιστότοπο. Πάτα «aA» στη γραμμή διευθύνσεων → Ρυθμίσεις ιστότοπου → Τοποθεσία → Ερώτηση και δοκίμασε ξανά. Αν είναι ήδη έτσι, έλεγξε Ρυθμίσεις → Απόρρητο και ασφάλεια → Υπηρεσίες τοποθεσίας → Ιστότοποι Safari.",
  "geo.denied.android":
    "Η τοποθεσία είναι μπλοκαρισμένη για αυτόν τον ιστότοπο. Πάτα το εικονίδιο κλειδαριάς δίπλα στη γραμμή διευθύνσεων → Άδειες → Τοποθεσία → Να επιτρέπεται. Αν επιτρέπεται ήδη, έλεγξε Ρυθμίσεις Android → Εφαρμογές → το πρόγραμμα περιήγησής σου → Άδειες → Τοποθεσία.",
  "geo.denied.desktop":
    "Η τοποθεσία είναι μπλοκαρισμένη για αυτόν τον ιστότοπο. Επίτρεψέ την από τις ρυθμίσεις ιστότοπου του προγράμματος περιήγησης — το εικονίδιο δίπλα στη γραμμή διευθύνσεων.",

  // ── Κίνηση ────────────────────────────────────────────────────────────────────────────────────
  "traffic.normal": "Χωρίς κίνηση",
  "traffic.slow": "Αργή κίνηση",
  "traffic.jam": "Πυκνή κίνηση",
  "traffic.unknown": "Άγνωστη κίνηση",

  // ── Χάρτης ────────────────────────────────────────────────────────────────────────────────────
  "map.boardHere": "ΕΠΙΒΙΒΑΣΗ ΕΔΩ",
  "map.getOffHere": "ΑΠΟΒΙΒΑΣΗ ΕΔΩ",
  "map.boardAt": "Επιβίβαση στο {place}",
  "map.getOffAt": "Αποβίβαση στο {place}",
  "map.towards": "προς {place}",
  "map.stopsAfterBoarding": { one: "1 στάση μετά την επιβίβαση", other: "{n} στάσεις μετά την επιβίβαση" },
  "map.departs": "Αναχώρηση {time}",
  "map.arrives": "Άφιξη {time}",
  "map.unavailable": "Ο χάρτης δεν είναι διαθέσιμος",
  "map.noApiKey": "Δεν έχει ρυθμιστεί κλειδί Google Maps API. Όρισε το GATSBY_GOOGLE_MAPS_API_KEY και επανεκκίνησε τον dev server.",
  "map.loadFailed": "Ο Χάρτης Google δεν φορτώθηκε — έλεγξε τους περιορισμούς παραπομπής του κλειδιού και ότι το Maps JavaScript API είναι ενεργό.",

  // ── Προηγούμενες διαδρομές ────────────────────────────────────────────────────────────────────
  "pastRides.title": "Οι διαδρομές σου",
  "pastRides.subtitle": "Όπου έχεις πάει με το carpool — ως οδηγός και ως επιβάτης.",
  "pastRides.roleLabel": "ΡΟΛΟΣ",
  "pastRides.whenLabel": "ΠΟΤΕ",
  "pastRides.role.all": "Όλα",
  "pastRides.role.drove": "Οδήγησα",
  "pastRides.role.rode": "Ταξίδεψα",
  "pastRides.when.7": "7 ημέρες",
  "pastRides.when.30": "30 ημέρες",
  "pastRides.when.all": "Συνολικά",
  "pastRides.empty.title": "Καμία διαδρομή δεν ταιριάζει με αυτά τα φίλτρα",
  "pastRides.empty.hint": "Δοκίμασε μεγαλύτερο εύρος χρόνου ή άλλαξε ρόλο.",
  "pastRides.count": { one: "1 διαδρομή", other: "{n} διαδρομές" },

  // ── Επιλογή ημερομηνίας & ώρας ────────────────────────────────────────────────────────────────
  "dtf.placeholder": "Διάλεξε ημερομηνία και ώρα",
  "dtf.dialogLabel": "Επιλογή ημερομηνίας και ώρας",
  "dtf.prevMonth": "Προηγούμενος μήνας",
  "dtf.nextMonth": "Επόμενος μήνας",
  "dtf.time": "Ώρα",
  "dtf.earlier15": "15 λεπτά νωρίτερα",
  "dtf.later15": "15 λεπτά αργότερα",
  "dtf.done": "Έτοιμο",

  // ── Σημείο παραλαβής ──────────────────────────────────────────────────────────────────────────
  "pickup.clear": "Καθαρισμός σημείου",
  "pickup.searchAddress": "Αναζήτησε διεύθυνση εκκίνησης…",
  "pickup.changeAddress": "Άλλαξε διεύθυνση εκκίνησης…",
  "pickup.unsupported": "Αυτό το πρόγραμμα περιήγησης δεν μπορεί να μοιραστεί την τοποθεσία σου — αναζήτησε τη διεύθυνση αντ' αυτού.",
  "pickup.unavailable": "Η τοποθεσία σου δεν είναι διαθέσιμη αυτή τη στιγμή — αναζήτησε τη διεύθυνση αντ' αυτού.",
  "pickup.timeout": "Αυτό άργησε πολύ. Δοκίμασε ξανά ή αναζήτησε τη διεύθυνση.",
  "pickup.failed": "Δεν ήταν δυνατός ο εντοπισμός σου — αναζήτησε τη διεύθυνση αντ' αυτού.",

  // ── Αλλαγή διαδρομής ──────────────────────────────────────────────────────────────────────────
  "edit.title": "Άλλαξε αυτή τη διαδρομή",
  "edit.titleReturn": "Άλλαξε την επιστροφή",
  "edit.everyoneTold": "Όσοι έχουν θέση ή έχουν ζητήσει μία ενημερώνονται για ό,τι άλλαξε.",
  "edit.returnPairedHint": "Η αλλαγή μετακινεί την επιστροφή, που είναι ξεχωριστή διαδρομή με δικούς της επιβάτες. Αν το αδειάσεις, ακυρώνεται εκείνη η διαδρομή.",
  "edit.returnNewHint": "Έτσι ανεβαίνει και δεύτερη διαδρομή προς την αντίθετη κατεύθυνση, για να βρουν οι συνάδελφοι επιστροφή.",
  "edit.until": "Μπορείς να κάνεις αλλαγές μέχρι {when}.",
  "edit.departure": "Αναχώρηση",
  "edit.return": "Επιστροφή (προαιρετικά)",
  "edit.noReturn": "Χωρίς επιστροφή",
  "edit.returnHintExisting":
    "Αλλάζοντάς το μετακινείς την επιστροφή, που είναι ξεχωριστή διαδρομή με δικούς της επιβάτες. Αν το καθαρίσεις, εκείνη η διαδρομή ακυρώνεται.",
  "edit.returnHintNew":
    "Ορίζοντάς το δημιουργείς δεύτερη διαδρομή προς την αντίθετη κατεύθυνση, ώστε οι συνάδελφοι να βρουν επιστροφή.",
  "edit.pickupPoint": "Σημείο παραλαβής",
  "edit.pickupNow": "Τώρα: {place}",
  "edit.pickupHintMoved": "Η μετακίνηση του σημείου παραλαβής αλλάζει τη διαδρομή, οπότε το μερίδιο καυσίμου και διοδίων υπολογίζεται ξανά.",
  "edit.pickupHintKeep": "Άφησέ το ως έχει για να κρατήσεις το τρέχον σημείο παραλαβής.",
  "edit.waysToGetThere": "{n} ΤΡΟΠΟΙ ΝΑ ΠΑΣ",
  "edit.tolls": "{amount} € διόδια",
  "edit.noTolls": "Χωρίς διόδια",
  "edit.routeHint": "Οι επιβάτες μοιράζονται το καύσιμο και τα διόδια του δρόμου που θα διαλέξεις, οπότε αυτό αλλάζει τι πληρώνουν.",
  "edit.notes": "Σημειώσεις για τους επιβάτες",
  "edit.notesPlaceholder": "Μουσική ναι, κατοικίδια όχι…",
  "edit.notesHint": "Αν αλλάξεις μόνο τις σημειώσεις, δεν ειδοποιείται κανείς.",
  "edit.saveChanges": "Αποθήκευση αλλαγών",

  // ── Μάρκα ──────────────────────────────────────────────────────────────────────────────────────
  /*
   * "T-Ride" stays Latin — it is the name. This line under it is copy, and follows the reader.
   *
   * This is the one string where the Greek is the original and the English is the translation,
   * which is the right direction for a product whose default language is Greek. «Θέλει παρέα» is
   * a real idiom — you say it of a meal, a walk, an evening — so it reads as something a person
   * wrote rather than a category label. It sells the feeling, and leaves the mechanism to the
   * screens that actually show it.
   *
   * No gendered word, so nothing to inflect wrongly — which two earlier attempts did not manage:
   * "το carpooling με το συνάδελφο" declined a masculine noun as neuter, and "ποτέ μόνος στον
   * δρόμο" addressed only half the workforce.
   *
   * 20 characters, the same as the English it replaces, so it still fits under the wordmark.
   */
  "brand.tagline": "ο δρόμος θέλει παρέα",

  // ── Πρόσφερε διαδρομή ─────────────────────────────────────────────────────────────────────────
  "offer.subtitle": "Ανέβασε μια διαδρομή — συνάδελφοι κοντά σου θα ζητήσουν να μπουν.",
  "offer.stretch.slow": "ένα αργό κομμάτι",
  "offer.stretch.jam": "μποτιλιάρισμα κατά τόπους",
  "offer.route.fuelEfficient": "Οικονομική",
  "offer.route.fastest": "Ταχύτερη",
  "offer.route.alternative": "Εναλλακτική",
  "offer.err.noPickup.title": "Δεν έχεις ορίσει σημείο εκκίνησης",
  "offer.err.noPickup.body":
    "Αναζήτησε μια διεύθυνση ή χρησιμοποίησε την τρέχουσα τοποθεσία σου — το σημείο παραλαβής είναι αυτό με το οποίο ταιριάζουν οι συνάδελφοι, οπότε δεν μπορεί να ανέβει διαδρομή χωρίς αυτό.",
  "offer.err.noCar.title": "Δεν έχεις ρυθμίσει το αυτοκίνητό σου",
  "offer.err.noCar.body":
    "Όλοι στο αυτοκίνητο μοιράζονται το καύσιμο, μαζί κι εσύ, οπότε πρέπει να ξέρουμε τι καταναλώνει πραγματικά πριν δώσουμε τιμή σε οποιονδήποτε. Χρειάζεται ένα πάτημα και δύο νούμερα στο Προφίλ.",
  "offer.adjusted.title": "Ανέβηκε, με προσαρμοσμένα νούμερα",
  "offer.adjusted.body":
    "Η διαδρομή που διάλεξες βρέθηκε εκτός των ορίων που μέτρησε ο διακομιστής για αυτό το ταξίδι, οπότε την τιμολόγησε με βάση τη δική του δρομολόγηση. Οι επιβάτες σου χρεώνονται αυτά τα ποσά, όχι την προεπισκόπηση.",
  "offer.seeTheRide": "Δες τη διαδρομή",
  "offer.err.notDriver": "Δεν είσαι ρυθμισμένος ως οδηγός",
  "offer.err.postFailed": "Δεν ήταν δυνατή η ανάρτηση της διαδρομής",
  "offer.err.serverRefused": "Ο διακομιστής δεν δέχτηκε τη διαδρομή. Έλεγξε την ώρα αναχώρησης και τις θέσεις και δοκίμασε ξανά.",
  "offer.addCarFirst": "Πρόσθεσε πρώτα το αυτοκίνητό σου",
  "offer.addCarBody":
    "Όλοι στο αυτοκίνητο μοιράζονται το καύσιμο, μαζί κι εσύ, οπότε πρέπει να ξέρουμε τι καταναλώνει πραγματικά πριν δώσουμε τιμή σε οποιονδήποτε. Χρειάζεται ένα πάτημα και δύο νούμερα.",
  "offer.setUpCar": "Ρύθμισε το αυτοκίνητό μου",
  "offer.trafficAt": "κίνηση στις {time}",
  "offer.seatsAvailable": "Διαθέσιμες θέσεις",
  "offer.alsoReturn": "Ανέβασε και διαδρομή επιστροφής",
  "offer.returnDeparture": "Αναχώρηση επιστροφής",
  "offer.pickReturnTime": "Διάλεξε ώρα επιστροφής",
  "offer.free.title": "Πρόσφερε αυτή τη διαδρομή δωρεάν",
  "offer.free.body": "Οι επιβάτες δεν πληρώνουν τίποτα — καλύπτεις εσύ το κόστος. Οι πόντοι εξακολουθούν να μετράνε.",
  "offer.tolls": "Διόδια",
  "offer.fromYourRoute": "από τη διαδρομή σου",
  "offer.avoidsTolls": "Η διαδρομή που διάλεξες αποφεύγει τα διόδια.",
  "offer.noTollRoads": "Δεν υπάρχουν διόδια σε αυτή τη διαδρομή.",
  "offer.tollsNeedPickup": "Διάλεξε σημείο εκκίνησης και θα υπολογίσουμε τα διόδια από τη διαδρομή.",
  "offer.notes": "Σημειώσεις (προαιρετικά)",
  "offer.notesPlaceholder": "Μουσική ναι, κάπνισμα όχι, κ.λπ.",
  "offer.needPickupTitle": "Αναζήτησε πρώτα διεύθυνση εκκίνησης",
  "offer.pickStartFirst": "Πρώτα διάλεξε αφετηρία",
  "offer.postRide": "Ανέβασε τη διαδρομή",
  "offer.preview.free": "Δωρεάν για τους επιβάτες",
  "offer.preview.split": "Πόσο θα κοστίσει",
  "offer.distance": "Απόσταση",
  "offer.costToRiders": "Κόστος για τους επιβάτες",
  "offer.eachRiderPays": "ΚΑΘΕ ΕΠΙΒΑΤΗΣ ΠΛΗΡΩΝΕΙ",
  "offer.eachPersonPays": "ΚΑΘΕ ΑΤΟΜΟ ΠΛΗΡΩΝΕΙ",
  "offer.coveringTrip": "Καλύπτεις εσύ τη διαδρομή — οι επιβάτες βλέπουν σήμανση «ΔΩΡΕΑΝ».",
  "offer.fuel": "Καύσιμο",
  "offer.fuelDetail": "Καύσιμο · {consumption} {unit}/100χλμ @ {price} €",
  "offer.totalTripCost": "Συνολικό κόστος διαδρομής",
  "offer.sharingTheCost": "Μοιρασιά κόστους",
  "offer.youPlusRiders": { one: "{total} (εσύ + 1 επιβάτης)", other: "{total} (εσύ + {n} επιβάτες)" },
  "offer.addCarHint": "Πρόσθεσε τι καταναλώνει το αυτοκίνητό σου και τι πληρώνεις ανά {unit} στο {profile} και αυτό συμπληρώνεται μόνο του.",
  "offer.yourProfile": "προφίλ σου",
  "offer.fromProfile": "Από το προφίλ σου — {change} αν δεν είναι αυτό το αυτοκίνητό σου.",
  "offer.changeThem": "άλλαξέ τα",
  "offer.ifAllFill": {
    one: "αν γεμίσει η θέση — {alone} ο καθένας αν έρθει ένας μόνο συνάδελφος. Πληρώνεις κι εσύ μερίδιο, οπότε πέφτει για όλους όσο έρχονται περισσότεροι.",
    other: "αν γεμίσουν και οι {n} θέσεις — {alone} ο καθένας αν έρθει ένας μόνο συνάδελφος. Πληρώνεις κι εσύ μερίδιο, οπότε πέφτει για όλους όσο έρχονται περισσότεροι.",
  },
  "offer.pointsHint": "Θα κερδίζεις {points} για κάθε επιβάτη που μεταφέρεις.",
  "offer.pointsAmount": "+{n} πόντους",

  // ── Λεπτομέρειες διαδρομής ────────────────────────────────────────────────────────────────────
  // Feminine, agreeing with "η διαδρομή" — the chip describes the ride.
  "rideStatus.OPEN": "Ανοιχτή",
  "rideStatus.FULL": "Γεμάτη",
  "rideStatus.IN_PROGRESS": "Σε εξέλιξη",
  "rideStatus.COMPLETED": "Ολοκληρώθηκε",
  "rideStatus.CANCELLED": "Ακυρώθηκε",

  "bookingStatus.PENDING": "Εκκρεμεί",
  "bookingStatus.CONFIRMED": "Επιβεβαιωμένη",
  "bookingStatus.REJECTED": "Απορρίφθηκε",
  "bookingStatus.CANCELLED": "Ακυρώθηκε",
  "bookingStatus.COMPLETED": "Ολοκληρώθηκε",

  "detail.transitToPickup": "Με ΜΜΜ στο σημείο παραλαβής",
  "detail.onFoot": "με τα πόδια",
  // "ΠΡΟΣ ΤΟ ΣΗΜΕΙΟ ΠΑΡΑΛΑΒΗΣ" is 24 caps characters and wraps on a 390px phone; this says the
  // same thing in 19 and reads as a question the rider is actually asking.
  "detail.gettingToPickup": "ΠΩΣ ΘΑ ΦΤΑΣΕΙΣ ΕΚΕΙ",
  /*
   * Written in capitals here, like every other all-caps label in this file, rather than left to
   * CSS `text-transform: uppercase`. Greek drops the accent on capitals — ΑΠΟ, not ΑΠΌ — and the
   * transform does not reliably do that, so "Από" was rendering as "ΑΠΌ", which is a spelling
   * error rather than a styling quirk. Writing the cased form makes the transform a no-op.
   */
  "detail.from": "ΑΠΟ",
  "detail.to": "ΠΡΟΣ",
  // The arrow is part of the string: Greek and English both read left-to-right here, but the arrow
  // belongs to the phrase rather than the layout, and a translator may want it dropped.
  "detail.viewArrow": "Δες τη →",
  "detail.complete": "Ολοκλήρωση",
  "detail.alsoCancelReturn": "Ακύρωσε και την επιστροφή",
  "detail.byTransit": "με ΜΜΜ",
  "detail.line": "γραμμή {line}",
  "detail.showSteps": "Δες τα βήματα",
  "detail.hideSteps": "Απόκρυψη βημάτων",
  "detail.takeLine": "Πάρε τη {line}",
  "detail.takeTransit": "Πάρε ΜΜΜ",
  "detail.fromStop": "από {stop}",
  "detail.toStop": "μέχρι {stop}",
  "detail.walkDistance": "Περπάτησε {distance}",
  "detail.stops": { one: "1 στάση", other: "{n} στάσεις" },
  "detail.arriveAt": "Άφιξη στο {place}",
  "detail.thePickupPoint": "σημείο παραλαβής",

  "detail.geoUnsupported": "Αυτό το πρόγραμμα περιήγησης δεν μπορεί να δείξει την τοποθεσία σου.",
  "detail.shareRefused": "Δεν ήταν δυνατή η κοινοποίηση της τοποθεσίας σου.",
  "detail.shareDropped": "Χάθηκε η σύνδεση κατά την κοινοποίηση της τοποθεσίας σου — συνεχίζουμε να προσπαθούμε.",
  "detail.geoDenied": "Δεν δόθηκε άδεια τοποθεσίας — επίτρεψέ την από τις ρυθμίσεις του προγράμματος περιήγησης.",
  "detail.geoUnavailable": "Η τοποθεσία δεν είναι διαθέσιμη αυτή τη στιγμή.",
  "detail.geoTimeout": "Το αίτημα τοποθεσίας άργησε πολύ.",

  "detail.notFound": "Η διαδρομή δεν βρέθηκε",
  "detail.backToRides": "Πίσω στις διαδρομές",
  "detail.back": "Πίσω",
  "detail.isTripHome": "Αυτή είναι η επιστροφή",
  "detail.hasTripHome": "Υπάρχει και επιστροφή",
  // "του πρωινού" is the genitive of "το πρωινό" — *breakfast*. The adjective is what was meant.
  "detail.seeRideOut": "Δες την πρωινή διαδρομή",
  "detail.leavesBookSeparately": "Φεύγει {time} — κλείσ' την ξεχωριστά",
  "detail.cantShowLocation": "Δεν μπορούμε να δείξουμε πού βρίσκεσαι",
  "detail.stillLooking": "Ακόμη ψάχνουμε την τοποθεσία σου",

  "detail.nothingToChange.title": "Καμία αλλαγή",
  "detail.nothingToChange.body": "Η διαδρομή είναι ήδη όπως την είχες ορίσει, οπότε δεν ειδοποιήθηκε κανείς.",
  "detail.changed.departure": "Η αναχώρηση είναι τώρα {when}.",
  "detail.changed.pickup": "Το σημείο παραλαβής μετακινήθηκε — τα καύσιμα και τα διόδια υπολογίστηκαν ξανά.",
  "detail.changed.seats": { one: "Προσφέρεις τώρα 1 θέση.", other: "Προσφέρεις τώρα {n} θέσεις." },
  "detail.changed.returnRemoved": "Η επιστροφή αφαιρέθηκε — οι επιβάτες θα χρειαστούν άλλον τρόπο να γυρίσουν.",
  "detail.changed.returnMoved": "Η ώρα επιστροφής άλλαξε.",
  "detail.changed.returnAdded": "Προστέθηκε διαδρομή επιστροφής.",
  "detail.changed.notes": "Οι σημειώσεις σου ενημερώθηκαν.",
  "detail.updated.title": "Η διαδρομή ενημερώθηκε",
  "detail.updated.notified": "Όλοι όσοι έχουν ή περιμένουν θέση ειδοποιήθηκαν.",
  "detail.updated.notNotified": "Δεν ειδοποιήθηκε κανείς — οι σημειώσεις από μόνες τους δεν αλλάζουν τη συμφωνία.",

  "detail.cancelRide.title": "Ακύρωση αυτής της διαδρομής;",
  "detail.cancelRide.withRiders": {
    one: "1 επιβάτης βασίζεται σε αυτή τη διαδρομή. Θα ειδοποιηθεί αμέσως και θα χρειαστεί να βρει άλλον τρόπο. Αυτό δεν αναιρείται.",
    other: "{n} επιβάτες βασίζονται σε αυτή τη διαδρομή. Θα ειδοποιηθούν αμέσως και θα χρειαστεί να βρουν άλλον τρόπο. Αυτό δεν αναιρείται.",
  },
  "detail.cancelRide.noRiders": "Κανείς δεν έχει κλείσει θέση ακόμη, οπότε δεν θα μείνει κανείς χωρίς μεταφορά. Αυτό δεν αναιρείται.",
  "detail.cancelRide.confirm": "Ακύρωση διαδρομής",
  "detail.cancelRide.keep": "Κράτησέ την",

  "detail.driverSubtitle": "Όμιλος ΟΤΕ · οδηγός",
  "detail.call": "Κάλεσε {name}",
  "detail.callShort": "Κλήση",
  "detail.walkToPickup": "Με τα πόδια στο σημείο",
  "detail.arrives": "Άφιξη",
  "detail.leavesTraffic": "φεύγει {time} · αναμενόμενη κίνηση",
  "detail.leavesEstimated": "φεύγει {time} · εκτίμηση",
  "detail.freeRide": "Δωρεάν διαδρομή",
  "detail.yourShare": "Το μερίδιό σου",
  "detail.youWaived": "παραιτήθηκες από τον επιμερισμό",
  "detail.driverWaived": "ο οδηγός παραιτήθηκε από τον επιμερισμό",
  "detail.splitWays": "{total} € μοιρασμένα σε {n}",
  "detail.allYours": "όλα δικά σου προς το παρόν — {each} € ο καθένας αν έρθει ένας συνάδελφος",
  "detail.splitTwoWays": "{total} € μοιρασμένα στα δύο μόλις μπεις",
  "detail.booked": "{booked}/{total} κλεισμένες",
  "detail.returnAt": "Επιστροφή {time}",

  "detail.findAnother": "Βρες άλλη",
  "detail.clash.title": "Είσαι ήδη σε διαδρομή εκείνη τη μέρα",
  "detail.clash.body":
    "Έχεις θέση στη διαδρομή του/της {name} στις {when}. Άφησε πρώτα εκείνη αν προτιμάς αυτή — κρατώντας δύο θέσεις αφήνεις έναν συνάδελφο εκτός και από τις δύο.",
  "detail.anotherRide": "άλλη διαδρομή",
  "detail.goToThatRide": "Δες εκείνη τη διαδρομή",
  "detail.requestAgain": "Ζήτησε ξανά",
  "detail.requestToJoin": "Ζήτησε να μπεις",
  "detail.cancelMyRequest": "Ακύρωση του αιτήματός μου",
  "detail.leaveRide": "Αποχώρηση από τη διαδρομή",
  "detail.stopSharing": "Διακοπή κοινοποίησης",
  "detail.shareLocation": "Κοινοποίηση τοποθεσίας",
  "detail.more": "Περισσότερα",
  "detail.tooCloseToEdit": "Πολύ κοντά στην αναχώρηση για αλλαγές σε αυτή τη διαδρομή.",
  "detail.changeDetails": "Αλλαγή στοιχείων",
  "detail.cancelRideShort": "Ακύρωση διαδρομής",
  "detail.locked": "Η διαδρομή έκλεισε — δεν γίνονται πια αλλαγές.",
  "detail.riders": "Επιβάτες",
  "detail.noBookings": "Καμία κράτηση ακόμη.",
  "detail.acceptRequest": "Αποδοχή του αιτήματος του/της {name}",
  "detail.rejectRequest": "Απόρριψη του αιτήματος του/της {name}",
  "detail.thisRider": "αυτού του επιβάτη",

  "detail.live.offline": "Η ζωντανή ροή αποσυνδέθηκε",
  "detail.live.reconnecting": "Επανασύνδεση στη ζωντανή ροή…",
  "detail.live.waiting": "Αναμονή για το πρώτο στίγμα GPS…",
  "detail.live.sharing": "Κοινοποιείς την τοποθεσία σου · {kph} χλμ/ώ",
  "detail.live.driverMoving": "Ο οδηγός κινείται · {kph} χλμ/ώρα",
  "detail.live.updated": "ενημερώθηκε {time}",
  /*
   * The building has a Greek name and it is the one people use: Μέγαρο ΟΤΕ. Earlier this file
   * treated "OTE HQ" as a proper noun to be left alone, which is true of the *company* — ΟΤΕ is
   * written ΟΤΕ — but not of the building, and not of the street. A Greek reader navigating to
   * Κηφισίας 99 should not be reading the address in English.
   */
  "office.name": "Μέγαρο ΟΤΕ — Κηφισίας 99, Μαρούσι",
  "detail.marker.driverLive": "Οδηγός — ζωντανά",
  "detail.marker.youAreHere": "Είσαι εδώ",
  // Split on {place} at the call site so the place name can be bold — the placeholder must survive.
  "rides.usingInstead": "Ξεκινάς από {place} αντί για το σπίτι σου",
  "rides.showFull": { one: "+ 1 γεμάτη διαδρομή", other: "+ {n} γεμάτες διαδρομές" },
  "rides.hideFull": "Απόκρυψη γεμάτων διαδρομών",
  "rides.marker.office": "Μέγαρο ΟΤΕ",
  "rides.marker.driverLeaves": "{name} · φεύγει {time}",
  // Reads inside "Πιο κοντά σε …", so it is the accusative, not the nominative "το σπίτι σου".
  "rides.yourHome": "το σπίτι σου",
  "offer.destinationIs": "Προορισμός:",
  // Both sides are pre-formatted by the caller — already carrying χλμ and the € symbol — so these
  // are frames, not sentences with units baked in.
  "offer.adjusted.distance": "Απόσταση {from} → {to}",
  "offer.adjusted.tolls": "Διόδια {from} € → {to} €",
  "offer.driveMins": "~{n} λεπτά με το αυτοκίνητο",
  "rides.marker.yourPickup": "Το σημείο παραλαβής σου",
  "rides.marker.home": "Το σπίτι σου",

  // ── Σφάλματα δικτύου ──────────────────────────────────────────────────────────────────────────
  "http.generic": "Αυτό δεν πέτυχε. Δοκίμασε ξανά.",
  "http.timeout": "Η απάντηση άργησε πολύ. Έλεγξε τη σύνδεσή σου και δοκίμασε ξανά.",
  "http.offline": "Δεν είναι δυνατή η σύνδεση με τον διακομιστή. Έλεγξε τη σύνδεσή σου και δοκίμασε ξανά.",
  "http.400": "Κάτι σε αυτό το αίτημα δεν ήταν σωστό. Έλεγξε τη φόρμα και δοκίμασε ξανά.",
  "http.401": "Η συνεδρία σου έληξε. Συνδέσου ξανά.",
  "http.403": "Δεν έχεις πρόσβαση σε αυτό.",
  "http.404": "Δεν το βρήκαμε — μπορεί να ακυρώθηκε ή να αφαιρέθηκε.",
  "http.409": "Κάποιος άλλος πρόλαβε. Ανανέωσε για να δεις πού βρίσκονται τα πράγματα.",
  "http.413": "Αυτό είναι πολύ μεγάλο για αποστολή.",
  "http.429": "Πάρα πολλές προσπάθειες. Περίμενε λίγο πριν δοκιμάσεις ξανά.",
  "http.500": "Κάτι πήγε στραβά από την πλευρά μας. Δοκίμασε ξανά σε λίγο.",
  "http.502": "Ο διακομιστής δεν είναι προσβάσιμος αυτή τη στιγμή. Δοκίμασε ξανά σε λίγο.",
  "http.503": "Ο διακομιστής είναι απασχολημένος ή επανεκκινεί. Δοκίμασε ξανά σε λίγο.",
  "http.504": "Ο διακομιστής άργησε πολύ να απαντήσει. Δοκίμασε ξανά σε λίγο.",

  // ── Πλοήγηση ──────────────────────────────────────────────────────────────────────────────────
  // Short on purpose: these sit under an icon in a five-item bar, and the Greek words for them are
  // longer than the English. "Ειδοποιήσεις" is already at the limit of what fits.
  "nav.home": "Αρχική",
  "nav.rides": "Διαδρομές",
  "nav.notifications": "Ειδοποιήσεις",
  "nav.leaders": "Κατάταξη",
  "nav.profile": "Προφίλ",
  "nav.openMenu": "Άνοιγμα μενού",
  "nav.closeMenu": "Κλείσιμο μενού",
  "nav.menu": "Μενού πλοήγησης",
  "nav.loading": "Φορτώνει το T-Ride σου…",
  "nav.badge.awaiting": { one: "1 περιμένει την απόφασή σου", other: "{n} περιμένουν την απόφασή σου" },
  "nav.badge.unread": { one: "1 μη αναγνωσμένη", other: "{n} μη αναγνωσμένες" },
  "nav.pointsAndStreak": "{points} πόντοι · σερί {weeks} εβδ.",

  // ── Σύνδεση ───────────────────────────────────────────────────────────────────────────────────
  "auth.workEmail": "Εταιρικό email",
  "auth.password": "Κωδικός",
  "login.pageTitle": "Σύνδεση",
  "login.welcomeBack": "Καλώς ήρθες ξανά",
  "login.welcomeHint": "Συνδέσου για να συνεχίσεις να μοιράζεσαι διαδρομές προς το Μαρούσι.",
  "login.blurb":
    "Το T-Ride σε βοηθά να μοιράζεσαι τη διαδρομή για τη δουλειά με συναδέλφους στο Μαρούσι. Για την ώρα λειτουργεί δοκιμαστικά. Συνδέσου με το εταιρικό σου email για να βρεις τις διαδρομές των συναδέλφων σου.",
  "login.fromNetwork": "ΑΠΟ ΤΟ ΔΙΚΤΥΟ",
  "login.quote.1": "Η Μαρία από τον ΟΤΕ μοιράστηκε διαδρομές με 47 συναδέλφους αυτόν τον μήνα.",
  "login.quote.2": "Εξοικονόμησα 34€ στη μετακίνηση προς το Μαρούσι μοιράζοντας τις διαδρομές Τρίτης-Πέμπτης.",
  "login.quote.3": "Το CO₂ της ομάδας μας από τις μετακινήσεις μειώθηκε 28% σε 6 εβδομάδες.",
  "login.forgot": "Ξέχασες;",
  "login.signIn": "Σύνδεση",
  "login.sessionExpired": "Η συνεδρία σου έληξε. Συνδέσου ξανά.",
  "login.passwordChanged": "Ο κωδικός άλλαξε. Συνδέσου με τον νέο σου κωδικό.",
  "login.failed": "Δεν ήταν δυνατή η σύνδεση. Έλεγξε τα στοιχεία σου.",
  "login.demo.title": "ΔΟΚΙΜΑΣΤΙΚΟΙ ΛΟΓΑΡΙΑΣΜΟΙ (ΜΟΝΟ ΓΙΑ ΔΟΚΙΜΕΣ)",
  "login.demo.hint": "Πάτα έναν για να συμπληρωθεί αυτόματα — κωδικός",

  // ── Επαναφορά κωδικού ─────────────────────────────────────────────────────────────────────────
  // Masculine, agreeing with "ο κωδικός" — the meter describes the password.
  "password.strength.tooShort": "Πολύ σύντομος",
  "password.strength.weak": "Αδύναμος",
  "password.strength.ok": "Μέτριος",
  "password.strength.good": "Καλός",
  "password.strength.strong": "Ισχυρός",
  "reset.pageTitle": "Επαναφορά κωδικού",
  "reset.step1.title": "Ξέχασες τον κωδικό σου;",
  "reset.step1.hint": "Βάλε το εταιρικό σου email και θα σου στείλουμε έναν 6ψήφιο κωδικό για επαναφορά.",
  "reset.sendCode": "Αποστολή κωδικού",
  "reset.step2.title": "Έλεγξε το email σου",
  "reset.step2.hint": "Αν υπάρχει λογαριασμός για {email}, στείλαμε έναν 6ψήφιο κωδικό. Λήγει σε 10 λεπτά.",
  "reset.codeLabel": "6ψήφιος κωδικός",
  "reset.verifyCode": "Επαλήθευση κωδικού",
  "reset.changeEmail": "Αλλαγή email",
  "reset.resendCode": "Στείλε μου νέο κωδικό",
  "reset.step3.title": "Όρισε νέο κωδικό",
  "reset.step3.hint": "Τουλάχιστον 8 χαρακτήρες. Θα αποσυνδεθείς από όλες τις άλλες συσκευές.",
  "reset.newPassword": "Νέος κωδικός",
  "reset.confirmPassword": "Επιβεβαίωση κωδικού",
  "reset.submit": "Επαναφορά κωδικού",
  "reset.err.send": "Δεν ήταν δυνατή η αποστολή κωδικού. Δοκίμασε ξανά.",
  "reset.err.code": "Αυτός ο κωδικός δεν είναι έγκυρος. Έλεγξέ τον και δοκίμασε ξανά.",
  "reset.err.mismatch": "Οι κωδικοί δεν ταιριάζουν.",
  "reset.err.tooShort": "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.",
  "reset.err.failed": "Δεν ήταν δυνατή η επαναφορά του κωδικού σου. Ξεκίνα από την αρχή.",

  // ── Αρχική ────────────────────────────────────────────────────────────────────────────────────
  "dash.title": "Γεια σου {name}",
  "dash.nextRideIs": "Η επόμενη διαδρομή σου είναι {when}.",
  "dash.postedRides": {
    one: "1 συνάδελφος ανέβασε διαδρομή — δες αν σε βολεύει.",
    other: "{n} συνάδελφοι ανέβασαν διαδρομές — διάλεξε μία προς τα εκεί που πας.",
  },
  "dash.quietDay": "Ήσυχη μέρα — οι οδηγοί συνήθως ανεβάζουν διαδρομές το βράδυ.",

  "dash.countdown.departed": "αναχώρησε",
  "dash.countdown.minutes": "σε {n} λεπτά",
  "dash.countdown.hours": "σε {h}ω",
  "dash.countdown.hoursMinutes": "σε {h}ω {m}λ",
  "dash.countdown.tomorrow": "αύριο",
  "dash.countdown.days": "σε {n} μέρες",

  "dash.walk": "{mins} με τα πόδια",
  "dash.transit": "{mins} με ΜΜΜ",
  "dash.headline.driving": "Οδηγείς {booked}/{total} επιβάτες",
  "dash.headline.requestSent": "Το αίτημα στάλθηκε στον/στην {name}",
  "dash.headline.ridingWith": "Ταξιδεύεις με {name}",

  "dash.yourNextRide": "Η ΕΠΟΜΕΝΗ ΣΟΥ ΔΙΑΔΡΟΜΗ",
  "dash.rideNofM": "ΔΙΑΔΡΟΜΗ {n} ΑΠΟ {total}",
  "dash.state.pending": "ΕΚΚΡΕΜΕΙ",
  "dash.state.driving": "ΟΔΗΓΕΙΣ",
  "dash.state.confirmed": "ΕΠΙΒΕΒΑΙΩΜΕΝΗ",
  "dash.pendingHint": "Θα σε ειδοποιήσουμε μόλις ο οδηγός επιβεβαιώσει — η θέση σου δεν έχει κρατηθεί ακόμη.",
  "dash.leavesFrom": "Φεύγει {time} από {place}",
  "dash.pickUpAt": "Παραλαβή στο {place} · φεύγει {time}",
  "dash.yourPickup": "το σημείο σου",
  // A verb, not a participle: "Καλώς όρισες" works for any reader. "Καλωσορισμένος/η" would need
  // to know the reader's gender, which the app deliberately never asks for.
  "dash.greeting": "Καλώς όρισες",
  "dash.seatsTaken": "{booked}/{total} θέσεις πιασμένες",
  "dash.free": "Δωρεάν",
  "dash.showDetails": "Λεπτομέρειες",
  "dash.hideDetails": "Απόκρυψη",
  "dash.prevRides": "Προηγούμενες διαδρομές",
  "dash.nextRides": "Επόμενες διαδρομές",

  "dash.ridepoints": "ΠΟΝΤΟΙ",
  "dash.seeRanking": "Δες την κατάταξη",
  "dash.stat.saved": "Εξοικονόμηση",
  "dash.stat.co2": "CO₂ που αποφεύχθηκε",
  "dash.stat.rides": "Διαδρομές",

  "dash.noRideBooked": "Καμία κρατημένη διαδρομή",
  "dash.upcomingRides": {
    one: "1 επερχόμενη διαδρομή προς το γραφείο — ρίξε μια ματιά.",
    other: "{n} επερχόμενες διαδρομές προς το γραφείο — διάλεξε μία κοντά σου.",
  },
  "dash.noUpcomingRides": "Καμία επερχόμενη διαδρομή ακόμη — γίνε ο πρώτος που θα ανεβάσει μία.",
  // Fallbacks for a failed load, when humanError has nothing more specific (no network or status sentence).
  "dash.feedLoadFailed": "Δεν ήταν δυνατή η φόρτωση των διαδρομών. Δοκίμασε ξανά σε λίγο.",
  "dash.commitmentsLoadFailed": "Δεν ήταν δυνατή η φόρτωση των δικών σου διαδρομών. Δοκίμασε ξανά σε λίγο.",
  "dash.findRide": "Βρες διαδρομή",
  "dash.offerRide": "Πρόσφερε διαδρομή",

  "dash.setHome.cta": "Όρισε τη διεύθυνσή σου στο Προφίλ",
  "dash.setHome.why": " ώστε να ταξινομούνται με βάση την απόσταση του σημείου παραλαβής από εσένα.",
  "dash.setHome.ai": "Προς το παρόν ταξινομούνται με βάση τις συνήθεις ώρες σου και με ποιους έχεις ταξιδέψει.",
  "dash.setHome.heuristic": "Μέχρι τότε ταξινομούνται απλώς με βάση το ποια φεύγει πρώτη.",
  "dash.matches.ai": "Προτάσεις AI για σένα",
  "dash.matches.smart": "Έξυπνες προτάσεις για σένα",
  "dash.matches.soonest": "Φεύγουν πρώτες",
  "dash.seeAll": "Δες όλες →",

  "dash.loader.title": "Βρίσκουμε τις καλύτερες προτάσεις",
  "dash.loader.pickup": "Διαβάζουμε το σημείο παραλαβής σου…",
  "dash.loader.history": "Μελετάμε τις προηγούμενες διαδρομές σου…",
  "dash.loader.matching": "Ταιριάζουμε τις καλύτερες διαδρομές για σένα…",
  "dash.loader.ranking": "Σχεδόν έτοιμο — ταξινόμηση κατά καταλληλότητα…",

  // ── Κατάταξη ──────────────────────────────────────────────────────────────────────────────────
  "leaders.title": "Κατάταξη",
  // "ride by ride" has no Greek reduplicative in this shape; "μία … τη φορά" is the idiom.
  /*
   * No companies here either. Leaderboard.js runs in single-company mode and renders only the
   * individuals board — the companies endpoint exists in the API for a future multi-tenant
   * rollout and nothing calls it — so naming companies promised a table that is never drawn.
   */
  "leaders.subtitle": "Οι συνάδελφοι που κινούν το Μαρούσι, μία διαδρομή τη φορά.",
  "leaders.yourRanking": "Η ΚΑΤΑΤΑΞΗ ΣΟΥ",
  "leaders.pts": "πόντοι",
  "leaders.pointsShort": { one: "1 πόντος", other: "{n} πόντοι" },
  "leaders.delta.week": "+{n} αυτή την εβδομάδα",
  "leaders.delta.month": "+{n} αυτόν τον μήνα",
  "leaders.delta.period": "+{n} αυτή την περίοδο",
  "leaders.overtake": "Σου λείπουν {points} για να προσπεράσεις τον/την {name} — περίπου {drives} διαδρομές ακόμη.",
  // Modern Greek uses the deponent υπερασπίζομαι; the active imperative is katharevousa and reads
  // archaic in a leaderboard nudge. "Κράτα" is plainer and unambiguous.
  "leaders.topSpot": "Είσαι #1 — κράτα τη θέση σου αυτή την εβδομάδα.",
  // Said to whoever is ranked below the last place the board shows: there is no one named above
  // them here, which is not the same thing as being first.
  "leaders.offBoard": "Η κατάταξη δείχνει λιγότερες θέσεις από τη δική σου — η θέση σου μετράει κανονικά.",
  "leaders.notRanked.title": "Δεν είσαι ακόμη σε αυτή την κατάταξη",
  "leaders.notRanked.windowHint": "Δεν έχεις κερδίσει πόντους σε αυτή την περίοδο. Μπες ή πρόσφερε μια διαδρομή και θα εμφανιστείς εδώ.",
  "leaders.notRanked.allHint": "Κάνε την πρώτη σου κοινή διαδρομή και θα εμφανιστείς στην κατάταξη.",
  "leaders.empty.title": "Καμία καταχώρηση ακόμη",
  "leaders.empty.windowHint": "Κανείς δεν κέρδισε πόντους σε αυτή την περίοδο. Δοκίμασε μεγαλύτερη, ή γίνε ο πρώτος.",
  "leaders.empty.allHint": "Μόλις οι συνάδελφοι αρχίσουν να μοιράζονται διαδρομές, η κατάταξη θα εμφανιστεί εδώ.",
  "leaders.timeframe.week": "Εβδομάδα",
  "leaders.timeframe.month": "Μήνας",
  "leaders.timeframe.all": "Συνολικά",
  "leaders.you": "ΕΣΥ",
  "leaders.podium": "Κορυφή της κατάταξης",
  "leaders.rules.title": "Πώς δουλεύουν οι πόντοι",
  "leaders.rules.carry": "για κάθε επιβάτη που παίρνεις μαζί σου",
  "leaders.rules.join": "για κάθε διαδρομή που κάνεις ως επιβάτης",

  // ── Λίστα διαδρομών ───────────────────────────────────────────────────────────────────────────
  "rides.later": "Αργότερα",
  "rides.title.today": "Οι σημερινές διαδρομές",
  "rides.title.tomorrow": "Οι αυριανές διαδρομές",
  "rides.title.later": "Επόμενες ημέρες",
  "rides.subtitle.sorted": "Ταξινομημένες με βάση την απόσταση από το σπίτι σου — διάλεξε θέση και πήγαινε στη δουλειά χωρίς να οδηγήσεις.",
  "rides.subtitle.setHome": "Όρισε τη διεύθυνσή σου στο Προφίλ για να βλέπεις πρώτα τις πιο κοντινές διαδρομές.",
  "rides.yourUpcoming": "Οι επερχόμενες διαδρομές σου",
  "rides.searchPlaceholder": "Άλλο σημείο εκκίνησης",
  // The header over the address suggestions. It was the literal "JUMP TO LOCATION" in the markup —
  // the one string the bilingual pass missed, so a Greek reader got an English shout mid-list.
  "rides.jumpTo": "ΠΗΓΑΙΝΕ ΣΕ ΣΗΜΕΙΟ",
  "rides.clearSearch": "Καθαρισμός αναζήτησης",
  "rides.resetToHome": "Επαναφορά",
  "rides.resetToHomeAria": "Επαναφορά στη διεύθυνσή σου",
  "rides.sort": "ΤΑΞΙΝΟΜΗΣΗ",
  "rides.closestTo": "Πιο κοντά σε {place}",
  // Its own entry because Greek writes "στο", not "σε το" — see the comment at the call site.
  "rides.closestToHome": "Πιο κοντά στο σπίτι σου",
  "rides.earliest": "Νωρίτερα",
  "rides.ranking": "Ταξινόμηση…",
  "rides.aiMatch": "Σου ταιριάζει",
  "rides.freeOnly": "Μόνο δωρεάν",
  "rides.showMap": "Εμφάνιση χάρτη",
  "rides.hideMap": "Απόκρυψη χάρτη",
  "rides.clearFocus": "Καθαρισμός εστίασης",
  "rides.showAll": "Δες όλες",
  "rides.empty.today": "Καμία διαδρομή σήμερα",
  "rides.empty.tomorrow": "Καμία διαδρομή αύριο",
  "rides.empty.later": "Καμία προγραμματισμένη διαδρομή αργότερα",
  "rides.empty.driverHint": "Γίνε ο πρώτος — ανέβασε διαδρομή και κέρδισε +{n} πόντους ανά επιβάτη.",
  "rides.empty.riderHint": "Δοκίμασε άλλη μέρα ή έλεγξε ξανά αργότερα — οι οδηγοί ανεβάζουν διαδρομές το βράδυ.",
  "rides.drvShort": "ΟΔΗ",
  "rides.rdrShort": "ΕΠΙ",
  "rides.waitingOn": "Περιμένεις απάντηση από {name}",
  "rides.confirmedWith": "Επιβεβαιωμένη με {name}",
  "rides.leavesAt": "Φεύγει {time}",
  "rides.roleDriver": "Οδηγός",
  "rides.rolePending": "Εκκρεμεί",
  "rides.roleRider": "Επιβάτης",

  // ── Κάρτα διαδρομής ───────────────────────────────────────────────────────────────────────────
  "ride.today": "Σήμερα",
  "ride.tomorrow": "Αύριο",
  "ride.mins": "{m} λ",
  "ride.hours": "{h} ω",
  "ride.hoursMins": "{h} ω {m} λ",
  "ride.dayAndDeparture": "{day} · φεύγει {time}",
  "ride.arrives": "άφιξη ~{time}",
  "ride.yours": "Η ΔΙΑΔΡΟΜΗ ΣΟΥ",
  "ride.requested": "ΖΗΤΗΘΗΚΕ",
  "ride.confirmed": "ΕΠΙΒΕΒΑΙΩΜΕΝΗ",
  "ride.rideWord": "διαδρομή",
  "ride.yourShare": "το μερίδιό σου",
  "ride.full": "Γεμάτη",
  "ride.seatsLeft": { one: "1 θέση", other: "{n} θέσεις" },

  // ── Ειδοποιήσεις ──────────────────────────────────────────────────────────────────────────────
  "notif.title": "Ειδοποιήσεις",
  // Greek requires the definite article on a generic subject; the article-less list was the
  // clearest English-speaker tell in the file.
  "notif.subtitle": "Τα αιτήματα συμμετοχής, οι επιβεβαιώσεις και οι αλλαγές διαδρομών εμφανίζονται εδώ.",
  "notif.loadFailed": "Δεν ήταν δυνατή η φόρτωση των ειδοποιήσεων.",
  // "αυτή…αυτή" in six words; the subject leads instead.
  "notif.actionFailed": "Η ενέργεια δεν ήταν δυνατή αυτή τη στιγμή.",
  // "All caught up" rendered literally describes data freshness, not an empty inbox.
  "notif.empty.title": "Τίποτα νέο",
  "notif.empty.hint": "Οι ειδοποιήσεις για τις διαδρομές και τα αιτήματά σου θα εμφανίζονται εδώ.",
  "notif.section.today": "Σήμερα",
  "notif.section.week": "Αυτή την εβδομάδα",
  "notif.section.older": "Παλαιότερα",
  "notif.showLess": "Λιγότερα",
  // The antecedent is "ειδοποιήσεις", feminine — every other "see all" in this file agrees.
  "notif.showAll": "Δες όλες",
  "notif.showAllMore": "Δες όλες (άλλες {n})",
  "notif.earned": "Κέρδισες {points}",
  "notif.points": { one: "+1 πόντο", other: "+{n} πόντους" },
  // Badges are upper-case chips in a fixed-width row; the Greek has to stay as short as the English.
  "notif.free": "ΔΩΡΕΑΝ",
  // 16 characters in a slot the English fills with 6; the noun alone is unambiguous here.
  "notif.pickup": "Παραλαβή",
  "notif.accept": "Αποδοχή",
  "notif.reject": "Απόρριψη",

  "notif.card.fallback": "Ενημέρωση διαδρομής",
  /*
   * A bare name, not "Ο/Η {name}".
   *
   * The article is grammatically the safer instinct, but a slash-disjunction at the head of every
   * card in a feed reads as a form field rather than a message — and the emails already do it the
   * other way (`mail.kind.INCOMING_REQUEST.subject` = "{0} θέλει να μπει…"), so the same event was
   * reaching the same person in two voices within seconds. Greek headline register allows a bare
   * proper noun as subject, so the frontend follows the backend.
   *
   * The exception is RIDE_COMPLETED, where the name is the *object* of a transitive verb: a bare
   * accusative name reads worse than the slash, so it keeps it.
   */
  "notif.card.INCOMING_REQUEST": "{name} θέλει να έρθει μαζί σου",
  // The driver confirms the passenger's *seat*, not their ride — and the email already says θέση,
  // so the two channels were contradicting each other about what had just happened.
  "notif.card.BOOKING_CONFIRMED": "{name} επιβεβαίωσε τη θέση σου",
  "notif.card.BOOKING_REJECTED": "{name} απέρριψε το αίτημά σου",
  "notif.card.RIDE_CANCELLED": "{name} ακύρωσε τη διαδρομή",
  "notif.card.BOOKING_CANCELLED": "{name} αποχώρησε από τη διαδρομή σου",
  "notif.card.BOOKING_COMPLETED": "Η διαδρομή σου με τον/την {name} ολοκληρώθηκε",
  // "Οδηγώ" takes a vehicle in Greek, never people.
  "notif.card.RIDE_COMPLETED": "Μετέφερες τον/την {name}",
  // "μια διαδρομή σου" claims the ride belongs to the reader, but the reader is the passenger.
  "notif.card.RIDE_CHANGED": "{name} άλλαξε διαδρομή στην οποία συμμετέχεις",
  "notif.card.DEPARTURE_REMINDER": "Η διαδρομή σου φεύγει σύντομα",

  "notif.badge.INCOMING_REQUEST": "ΕΚΚΡΕΜΕΙ",
  "notif.badge.BOOKING_CONFIRMED": "ΕΠΙΒΕΒΑΙΩΘΗΚΕ",
  "notif.badge.BOOKING_REJECTED": "ΑΠΟΡΡΙΦΘΗΚΕ",
  "notif.badge.RIDE_CANCELLED": "ΑΚΥΡΩΘΗΚΕ",
  "notif.badge.BOOKING_CANCELLED": "ΑΠΟΧΩΡΗΣΕ",
  "notif.badge.BOOKING_COMPLETED": "ΟΛΟΚΛΗΡΩΘΗΚΕ",
  "notif.badge.RIDE_COMPLETED": "ΟΛΟΚΛΗΡΩΘΗΚΕ",
  "notif.badge.RIDE_CHANGED": "ΑΛΛΑΞΕ",
  "notif.badge.DEPARTURE_REMINDER": "ΦΕΥΓΕΙ ΣΥΝΤΟΜΑ",

  "notif.resolution.CONFIRMED": "ΕΓΙΝΕ ΔΕΚΤΟ",
  "notif.resolution.REJECTED": "ΑΠΟΡΡΙΦΘΗΚΕ",
  "notif.resolution.CANCELLED": "ΑΠΟΣΥΡΘΗΚΕ",

  // ── Προφίλ ────────────────────────────────────────────────────────────────────────────────────
  "profile.title": "Προφίλ",
  "profile.subtitle":
    "Η ταυτότητά σου στο T‑Ride — όρισε από πού ξεκινάς, το τηλέφωνό σου και πώς μετακινείσαι.",
  "profile.org": "Όμιλος ΟΤΕ",
  "profile.role.driverRider": "Οδηγός · Συνεπιβάτης",
  "profile.role.riderOnly": "Μόνο συνεπιβάτης",
  "profile.points": "ΠΟΝΤΟΙ",
  "profile.streak": "σερί {n} εβδ.",

  "profile.pickup.label": "ΣΥΝΗΘΕΣ ΣΗΜΕΙΟ ΕΠΙΒΙΒΑΣΗΣ",
  "profile.pickup.hint":
    "Από εδώ ταξινομούμε τις διαδρομές κοντά σου. Ο οδηγός το βλέπει μόνο όταν κλείνεις θέση μαζί του χωρίς να διαλέξεις άλλο σημείο.",
  "profile.pickup.set": "Όρισε το συνηθισμένο σου σημείο επιβίβασης",
  "profile.pickup.change": "Άλλαξε το συνηθισμένο σου σημείο επιβίβασης",
  "profile.pickup.stopChanging": "Διακοπή αλλαγής του σημείου επιβίβασης",
  "profile.pickup.blurb":
    "Από εδώ ταξινομούμε τις διαδρομές κοντά σου. Ο οδηγός το βλέπει μόνο όταν κλείνεις θέση μαζί του χωρίς να διαλέξεις άλλο σημείο — γι' αυτό διάλεξε μια γωνία ή μια στάση, όχι την πόρτα σου.",
  "profile.pickup.searchPlaceholder": "Αναζήτησε δρόμο, γωνία ή σημείο αναφοράς…",
  "profile.pickup.currentLocation": "Τρέχουσα τοποθεσία",
  "profile.pickup.popular": "ΔΗΜΟΦΙΛΗ ΣΗΜΕΙΑ",
  "profile.pickup.discard": "Απόρριψη επιλεγμένης διεύθυνσης",
  "profile.pickup.saveFailed": "Δεν ήταν δυνατή η αποθήκευση των αλλαγών.",
  "profile.pickup.geo.unsupported": "Το πρόγραμμα περιήγησής σου δεν μπορεί να βρει την τοποθεσία σου.",
  "profile.pickup.geo.resolveFailed": "Δεν βρέθηκε η διεύθυνση — δοκίμασε να την πληκτρολογήσεις.",
  "profile.pickup.geo.denied": "Δεν δόθηκε άδεια — επίτρεψε την πρόσβαση στην τοποθεσία από το πρόγραμμα περιήγησης.",
  "profile.pickup.geo.unavailable": "Η τοποθεσία δεν είναι διαθέσιμη.",
  "profile.pickup.geo.timeout": "Το αίτημα τοποθεσίας άργησε πολύ.",
  "profile.pickup.geo.error": "Σφάλμα γεωεντοπισμού.",

  "profile.phone.label": "ΤΗΛΕΦΩΝΟ",
  "profile.phone.title": "Τηλέφωνο",
  "profile.phone.notSet": "Δεν έχει οριστεί — οι συνεπιβάτες που επιβεβαιώθηκαν δεν θα μπορούν να σε καλέσουν.",
  "profile.phone.visibility": "Ορατό μόνο σε όσους έχεις ταιριάξει σε κάποια διαδρομή.",
  "profile.phone.format": "Χρησιμοποίησε διεθνή μορφή. Τα ελληνικά κινητά μοιάζουν με",
  "profile.phone.placeholder": "+30 694 123 4567",
  "profile.phone.invalid": "Αυτό δεν μοιάζει με έγκυρο αριθμό τηλεφώνου.",
  "profile.phone.saveFailed": "Δεν ήταν δυνατή η αποθήκευση του τηλεφώνου σου.",
  "profile.phone.cancelAria": "Ακύρωση επεξεργασίας τηλεφώνου",

  "profile.driver.title": "Πηγαίνω στη δουλειά με αυτοκίνητο",
  "profile.driver.hint":
    "Μπορείς πάντα να συμμετέχεις σε διαδρομές ως επιβάτης. Ενεργοποιώντας το, ανοίγει το «Το αυτοκίνητό σου» παρακάτω, που υπολογίζει τη συνεισφορά του καθενός.",

  "profile.car.title": "Το αυτοκίνητό σου",
  "profile.car.hint": "Από εδώ υπολογίζεται η συνεισφορά του καθενός — και η δική σου.",
  "profile.car.hintSet": "Κράτησέ το κοντά σε αυτό που πράγματι πληρώνεις.",
  "profile.car.hintUnset": "Ξεκίνα από το τι καίει.",
  "profile.car.fuelQuestion": "Τι καίει;",
  "profile.car.fuelSet": "✓ ορίστηκε",
  "profile.car.fuelPick": "· διάλεξε ένα",
  "profile.car.pickOneHint": "Διάλεξε ένα — καθορίζει τη μονάδα και για τα δύο νούμερα παρακάτω.",
  "profile.car.consumption": "Κατανάλωση",
  "profile.car.pricePaid": "Τιμή που πλήρωσες τελευταία",
  "profile.car.perKm": "Οι διαδρομές σου κοστίζουν {price} ανά χλμ",
  "profile.car.electricFree":
    "Φορτίζεις στη δουλειά; Βάλε {zero} και οι διαδρομές σου δεν κοστίζουν τίποτα στους επιβάτες.",
  "profile.car.saveFailed": "Δεν ήταν δυνατή η αποθήκευση του αυτοκινήτου σου.",
  /*
   * Imperatives, not nouns. The call site passes `label.toLowerCase()` — the nominative
   * "κατανάλωση" — and "Αύξηση κατανάλωση" is ungrammatical: the noun demands a genitive it will
   * never be given. The imperative takes the accusative, which is identical to the nominative for
   * both fields, so this reads correctly with no change at the call site.
   */
  "profile.car.increase": "Αύξησε {field}",
  "profile.car.decrease": "Μείωσε {field}",
  // "Τα περισσότερα" is neuter plural with no antecedent — the field is η κατανάλωση or η τιμή,
  // both feminine singular. An impersonal verb sidesteps the agreement entirely.
  "profile.car.outOfBand":
    "Συνήθως κυμαίνεται μεταξύ {low} και {high} {unit}. Δεν πειράζει αν αυτό πληρώνεις πράγματι — από εδώ χρεώνονται οι επιβάτες.",

  "fuel.COMBUSTION": "Βενζίνη / Πετρέλαιο / Υβριδικό / Υγραέριο",
  "fuel.ELECTRIC": "Ηλεκτρικό",

  "profile.rideEmails.title": "Email διαδρομών",
  "profile.rideEmails.hint":
    "Αιτήματα συμμετοχής, επιβεβαιώσεις, ακυρώσεις και μια υπενθύμιση πριν την αναχώρηση. Οι ειδοποιήσεις εμφανίζονται πάντα μέσα στην εφαρμογή — αυτό ρυθμίζει μόνο τα email σου. Τα email ασφαλείας, όπως η επαναφορά κωδικού, στέλνονται πάντα.",
  "profile.rideEmails.aria": "Συχνότητα email διαδρομών",
  "emailPref.INSTANT.label": "Σε κάθε συμβάν",
  "emailPref.INSTANT.hint": "Ένα email μόλις κάτι συμβεί",
  "emailPref.DIGEST.label": "Ημερήσια σύνοψη",
  "emailPref.DIGEST.hint": "Μία σύνοψη κάθε πρωί",
  "emailPref.OFF.label": "Ανενεργά",
  "emailPref.OFF.hint": "Μόνο ειδοποιήσεις μέσα στην εφαρμογή",

  "profile.appearance.title": "Εμφάνιση",
  "profile.appearance.hint": "Εφαρμόζεται αμέσως, σε αυτή τη συσκευή. Ο χάρτης ακολουθεί την ίδια επιλογή.",
  "profile.appearance.aria": "Θέμα χρωμάτων",
  "theme.light.label": "Φωτεινό",
  "theme.light.hint": "Πάντα το φωτεινό θέμα",
  "theme.dark.label": "Σκούρο",
  "theme.dark.hint": "Πάντα το σκούρο θέμα",
  "theme.system.label": "Συσκευή",
  "theme.system.hint": "Ακολουθεί το κινητό ή τον υπολογιστή σου και αλλάζει μαζί τους",

  // Admin only. Unlike every other setting on this screen it is not personal, so the hint has to
  // say so before someone flips it expecting it to affect only themselves.
  // Numbered rather than "current"/"new": the app has not launched, so neither is current to anyone.
  // The hint under each says what it looks like, so the admin can picture the result.
  "profile.uiVersion.title": "Έκδοση εμφάνισης",
  "profile.uiVersion.hint": "Ισχύει για όλους, και στη σελίδα σύνδεσης. Αλλάζει αμέσως.",
  "profile.uiVersion.aria": "Έκδοση εμφάνισης εφαρμογής",
  "profile.uiVersion.v1.label": "Έκδοση 1",
  "profile.uiVersion.v1.hint": "Η αρχική εμφάνιση, με διάφανα πλαίσια",
  "profile.uiVersion.v2.label": "Έκδοση 2",
  "profile.uiVersion.v2.hint": "Η νέα εμφάνιση, με λευκές κάρτες και στρογγυλά κουμπιά",
  "profile.uiVersion.saveFailed": "Δεν ήταν δυνατή η αλλαγή έκδοσης — ισχύει ακόμη η προηγούμενη.",

  "profile.language.title": "Γλώσσα",
  "profile.language.hint":
    "Αλλάζει αμέσως την εφαρμογή και τα email που σου στέλνει το T‑Ride. Ακολουθεί τον λογαριασμό σου, οπότε ισχύει σε κάθε συσκευή που συνδέεσαι.",
  "profile.language.aria": "Γλώσσα εφαρμογής",
  "profile.language.saveFailed": "Δεν ήταν δυνατή η αποθήκευση της γλώσσας — θα επανέλθει μόλις ανανεώσεις τη σελίδα.",
  "profile.driver.saveFailed": "Η αλλαγή δεν αποθηκεύτηκε. Έλεγξε τη σύνδεσή σου και πάτα ξανά τον διακόπτη.",
  "profile.rideEmails.saveFailed": "Η επιλογή σου για τα email δεν αποθηκεύτηκε. Έλεγξε τη σύνδεσή σου και διάλεξε ξανά.",
  // The names on the station signs, which is what a driver navigating there will actually read.
  "profile.suggestion.halandri": "Μετρό Χαλανδρίου",
  "profile.suggestion.kifisia": "Πλατεία Κηφισιάς",
  "profile.suggestion.ambelokipoi": "Μετρό Αμπελοκήπων",

  "language.el.short": "ΕΛ",
  "language.el.label": "Ελληνικά",
  "language.el.hint": "Η γλώσσα της εφαρμογής και των email σου",
  "language.en.short": "EN",
  "language.en.label": "English",
  // The label stays "English" on purpose — a language is findable in its own name, which is what
  // rescues someone who switched by accident. The hint is not a name, so it follows the reader.
  "language.en.hint": "Η γλώσσα της εφαρμογής και των email σου",

  // ── Δημόσιες σελίδες (χειριστήρια κεφαλίδας) ──────────────────────────────────────────────────
  "public.themeToDark": "Αλλαγή σε σκούρο θέμα",
  "public.themeToLight": "Αλλαγή σε φωτεινό θέμα",

  "profile.pastRides.title": "Οι διαδρομές σου",
  "profile.pastRides.seeAll": "Δες και τις {n} →",
  "profile.pastRides.empty": "Καμία ολοκληρωμένη διαδρομή ακόμη — η πρώτη σου θα εμφανιστεί εδώ.",

  "profile.signedInAs": "Σύνδεση ως {email}",
  "profile.signOut": "Αποσύνδεση",

  // Profile photo. The error sentences mirror the server's avatar.* messages word for word, so a file
  // refused before upload reads exactly like one the server refused.
  "profile.photo.add": "Προσθήκη φωτογραφίας",
  "profile.photo.change": "Αλλαγή φωτογραφίας",
  "profile.photo.remove": "Αφαίρεση",
  "profile.photo.hint": "Τη βλέπουν μόνο οι συνάδελφοι που έχουν συνδεθεί. JPEG ή PNG, έως 25 MB.",
  "profile.photo.removeTitle": "Να αφαιρεθεί η φωτογραφία;",
  "profile.photo.removeBody": "Θα φαίνονται τα αρχικά σου.",
  "profile.photo.saveFailed": "Η φωτογραφία δεν αποθηκεύτηκε. Δοκίμασε ξανά.",
  "profile.photo.removeFailed": "Η φωτογραφία δεν αφαιρέθηκε. Δοκίμασε ξανά.",
  "profile.photo.error.empty": "Δεν επιλέχθηκε φωτογραφία. Διάλεξε ένα αρχείο εικόνας.",
  "profile.photo.error.tooLarge": "Η φωτογραφία είναι πολύ μεγάλη. Διάλεξε ένα αρχείο έως {mb} MB.",
  "profile.photo.error.type": "Αυτό το αρχείο δεν είναι φωτογραφία που ανοίγει εδώ. Διάλεξε μια φωτογραφία JPG ή PNG και δοκίμασε ξανά.",
  "profile.photo.error.webp": "Αυτός ο τύπος φωτογραφίας (WebP) δεν ανοίγει εδώ. Αποθήκευσέ τη ως JPG ή PNG και δοκίμασε ξανά.",
  "profile.photo.error.unreadable": "Δεν μπορέσαμε να διαβάσουμε αυτή τη φωτογραφία — ίσως είναι κατεστραμμένη. Δοκίμασε μια άλλη.",

  "profile.danger.title": "Επικίνδυνη ζώνη",
  "profile.danger.hint":
    "Η διαγραφή του λογαριασμού σου ακυρώνει κάθε διαδρομή που οδηγείς και σβήνει τα προσωπικά σου στοιχεία. Οι ολοκληρωμένες διαδρομές παραμένουν στο ιστορικό των συναδέλφων σου, χωρίς το όνομά σου. Δεν υπάρχει αναίρεση.",
  "profile.danger.delete": "Διαγραφή του λογαριασμού μου",
  "profile.danger.deleteFailed": "Δεν ήταν δυνατή η διαγραφή του λογαριασμού σου.",

  "deleteAccount.title": "Διαγραφή του λογαριασμού σου;",
  "deleteAccount.cancelledWord": "ακυρωθεί",
  "deleteAccount.point.rides": "Κάθε διαδρομή που οδηγείς θα {cancelled}, και οι επιβάτες σου θα ενημερωθούν.",
  "deleteAccount.point.seats": "Οι θέσεις που κρατάς σε διαδρομές άλλων θα αποδεσμευτούν.",
  "deleteAccount.point.details": "Το όνομα, το email, το τηλέφωνο και η διεύθυνσή σου διαγράφονται.",
  "deleteAccount.point.history":
    "Οι διαδρομές που έχεις ήδη ολοκληρώσει παραμένουν στο ιστορικό των συναδέλφων σου — ανώνυμα, γιατί είναι και δικά τους αρχεία.",
  // There is no sign-up to come back through any more, so the line no longer says "register again".
  "deleteAccount.noUndo": "Αυτό δεν αναιρείται — ο λογαριασμός δεν επανέρχεται.",
  "deleteAccount.passwordLabel": "Βάλε τον κωδικό σου για επιβεβαίωση",
  "deleteAccount.keep": "Κράτα τον λογαριασμό μου",
  "deleteAccount.confirm": "Διαγραφή λογαριασμού",

  "notFound.pageTitle": "Δεν βρέθηκε",
  "notFound.title": "Δεν βρήκαμε αυτή τη σελίδα",
  "notFound.body": "Ίσως ο σύνδεσμος είναι παλιός ή γράφτηκε λάθος. Γύρνα στην αρχική και συνέχισε από εκεί.",
  "notFound.publicBody": "Δεν βρήκαμε αυτή τη σελίδα. Ίσως ο σύνδεσμος είναι παλιός ή γράφτηκε λάθος. Γύρνα στην αρχική και συνέχισε από εκεί.",
  "notFound.backToDashboard": "Επιστροφή στην αρχική",
  "notFound.backHome": "Επιστροφή στην αρχική",

  "auth.showPassword": "Εμφάνιση κωδικού",
  "auth.hidePassword": "Απόκρυψη κωδικού",
  "reset.backToSignIn": "Επιστροφή στη σύνδεση",
  "dash.goToPosition": "Μετάβαση στη θέση {n} από {total}",

  "profile.footer.feedback": "Στείλε σχόλια",
  "profile.footer.version": "v0.1 · πιλοτικό T‑Ride",

  // Roles and the admin screen.
  "nav.pointsSystem": "Σύστημα πόντων",
  "admin.title": "Σύστημα πόντων",
  "admin.subtitle": "Εδώ ορίζεις πόσους πόντους δίνει μια διαδρομή, τι μετράει η Κατάταξη και ποιες επιβραβεύσεις κερδίζουν οι συνάδελφοι.",
  "admin.saved": "Οι ρυθμίσεις αποθηκεύτηκαν.",
  "admin.save": "Αποθήκευση",
  "admin.saving": "Γίνεται αποθήκευση…",
  "admin.discard": "Απόρριψη αλλαγών",
  "admin.you": "εσένα",
  "admin.lastChanged": "Τελευταία αλλαγή {when} από {who}.",
  "admin.err.load": "Δεν ήταν δυνατή η φόρτωση των ρυθμίσεων.",
  "admin.err.save": "Δεν ήταν δυνατή η αποθήκευση των ρυθμίσεων.",
  "admin.points.title": "Πόντοι",
  "admin.points.help": "Απονέμονται όταν ολοκληρώνεται μια διαδρομή. Οι αλλαγές ισχύουν μόνο για επόμενες διαδρομές — όσοι πόντοι έχουν ήδη δοθεί μένουν ως έχουν.",
  "admin.points.rider": "Πόντοι ανά επιβάτη",
  "admin.points.riderHint": "Τους παίρνει κάθε επιβάτης που επιβεβαιώθηκε, μόλις ολοκληρωθεί η διαδρομή. Οι συνάδελφοι το βλέπουν στην κάρτα «Πώς δουλεύουν οι πόντοι» της Κατάταξης.",
  "admin.points.driverPerRider": "Πόντοι οδηγού ανά επιβάτη",
  "admin.points.driverPerRiderHint": "Ο οδηγός τους παίρνει για κάθε επιβάτη που είχε μαζί του — με τρεις επιβάτες, τρεις φορές. Οι συνάδελφοι το βλέπουν στην κάρτα «Πώς δουλεύουν οι πόντοι» της Κατάταξης.",
  "admin.points.driverCompletion": "Πόντοι οδηγού για ολοκλήρωση",
  "admin.points.driverCompletionHint": "Ο οδηγός τους παίρνει μία φορά για κάθε διαδρομή που ολοκληρώνει, όσοι επιβάτες κι αν ήταν. Βάλε πάνω από 0 αν θέλεις να επιβραβεύεις και τον οδηγό που πρόσφερε θέσεις αλλά δεν ήρθε κανείς. Οι συνάδελφοι το βλέπουν στην κάρτα «Πώς δουλεύουν οι πόντοι».",
  "admin.ranking.title": "Κατάταξη",
  "admin.ranking.help": "Πόσες μέρες πίσω μετράει κάθε καρτέλα της Κατάταξης και πόσοι συνάδελφοι φαίνονται στη λίστα.",
  "admin.ranking.week": "Η «Εβδομάδα» καλύπτει (μέρες)",
  "admin.ranking.weekHint": "Πόσες τελευταίες μέρες μετράει η καρτέλα «Εβδομάδα» στην Κατάταξη.",
  "admin.ranking.month": "Ο «Μήνας» καλύπτει (μέρες)",
  "admin.ranking.monthHint": "Πόσες τελευταίες μέρες μετράει η καρτέλα «Μήνας» στην Κατάταξη.",
  "admin.ranking.boardSize": "Θέσεις που εμφανίζονται",
  "admin.ranking.boardSizeHint": "Πόσοι συνάδελφοι φαίνονται στη λίστα της Κατάταξης.",
  "admin.rewards.title": "Επιβραβεύσεις που κερδίζεις μία φορά",
  "admin.rewards.summary": "{n} πόντοι",
  "admin.recurring.title": "Επιβραβεύσεις που κερδίζεις ξανά και ξανά",
  "admin.recurring.help": "Κάθε φορά που κάποιος μαζεύει άλλους τόσους πόντους, την κερδίζει ξανά. Οι συνάδελφοι τις βλέπουν στην Κατάταξη.",
  "admin.recurring.empty": "Καμία ακόμη. Πρόσθεσε μία για να δίνεις κάτι ξανά και ξανά.",
  "admin.recurring.every": "Κάθε πόσους πόντους",
  "admin.recurring.everyHint": "Κάθε φορά που το σύνολο κάποιου συμπληρώνει άλλους τόσους πόντους, κερδίζει ξανά την επιβράβευση.",
  "admin.recurring.maxGrants": "Όριο φορών",
  "admin.recurring.maxGrantsHint": "Πόσες φορές το πολύ μπορεί να την κερδίσει ο καθένας. Άφησέ το 0 για χωρίς όριο.",
  "admin.recurring.summary": "κάθε {n} πόντους",
  "admin.recurring.add": "Πρόσθεσε επιβράβευση που ξανακερδίζεται",
  "admin.rewards.help": "Πόσους πόντους χρειάζεται κάθε επιβράβευση και τι παίρνει ο συνάδελφος. Οι συνάδελφοι τις βλέπουν στην Κατάταξη.",
  "admin.rewards.empty": "Καμία ακόμη. Οι συνάδελφοι δεν βλέπουν επιβραβεύσεις μέχρι να προσθέσεις μία.",
  "admin.rewards.threshold": "Πόντοι που χρειάζονται",
  "admin.rewards.label": "Όνομα επιβράβευσης",
  "admin.rewards.description": "Τι παίρνει",
  "admin.rewards.provider": "Διαχειρίζεται από",
  "admin.rewards.enabled": "Προσφέρεται",
  "admin.rewards.remove": "Αφαίρεση",
  "admin.rewards.addTier": "Πρόσθεσε επιβράβευση",
  "admin.rewards.addField": "Προσθήκη λεπτομέρειας",
  "admin.rewards.payload": "Λεπτομέρειες (ελεύθερο κείμενο — δεν χρησιμοποιείται ακόμη)",
  "offer.route.via": "μέσω {road}",

  // Live scheme rules on the leaderboard, and the reward-provider settings.
  "leaders.rules.complete": "για κάθε διαδρομή που ολοκληρώνεις ως οδηγός",
  "leaders.rules.rewards": "ΕΠΙΒΡΑΒΕΥΣΕΙΣ",
  "admin.stepper.increase": "Αύξηση {field}",
  "admin.stepper.decrease": "Μείωση {field}",
  "admin.rewards.optional": "(προαιρετικό)",
  "admin.provider.box.name": "Box",
  "admin.provider.box.hint": "Δίνει κουπόνι Box. Για την ώρα είναι δοκιμαστικό: φτιάχνεται κωδικός, αλλά το Box δεν ενημερώνεται ακόμη.",

  // The reward ladder on the leaderboard.
  "rewards.title": "ΕΠΙΒΡΑΒΕΥΣΕΙΣ",
  "rewards.collapsed.next": "{n} πόντοι για {reward}",
  "rewards.collapsed.none": "Τις έχεις πάρει όλες",
  "rewards.section.recurring": "ΞΑΝΑ ΚΑΙ ΞΑΝΑ",
  "rewards.section.recurringHint": "Κάθε φορά που συμπληρώνεις τους πόντους, το κερδίζεις ξανά.",
  "rewards.section.ladder": "ΣΤΟΧΟΙ",
  "rewards.section.ladderHint": "Κερδίζονται μία φορά, μόλις φτάσεις το όριο.",
  "rewards.ladderTitle": "Επιβραβεύσεις με στόχο πόντων",
  "rewards.ladder.moreEarned": { one: "+1 ακόμη κερδισμένο", other: "+{n} ακόμη κερδισμένα" },
  "rewards.ladder.hideEarned": "Απόκρυψη παλαιότερων",
  "rewards.recurring.completedCount": { one: "1 ολοκληρωμένη επιβράβευση", other: "{n} ολοκληρωμένες επιβραβεύσεις" },
  "rewards.recurring.hideCompleted": "Απόκρυψη ολοκληρωμένων",
  "rewards.recurring.every": "κάθε {n} πόντους",
  "rewards.recurring.everyCapped": "κάθε {n} πόντους · έως {max} φορές",
  "rewards.recurring.toNext": "πόντοι για το επόμενο",
  "rewards.recurring.earnedSoFar": { one: "1 μέχρι τώρα", other: "{n} μέχρι τώρα" },
  "rewards.recurring.allClaimed": { one: "Το πήρες — η προσφορά ολοκληρώθηκε.", other: "Τα πήρες όλα — {n} φορές." },
  "rewards.recurring.ridesToGo": { one: "1 ακόμη κοινή διαδρομή", other: "Περίπου {n} ακόμη κοινές διαδρομές" },
  "rewards.toGo": "πόντοι για {reward}",
  "rewards.allEarned": "Τις κέρδισες όλες — μπράβο σου.",
  "rewards.earned": "Κερδισμένο",
  "rewards.earnedCount": { one: "1 κερδισμένο", other: "{n} κερδισμένα" },
  "admin.err.missing": "Συμπλήρωσε πρώτα τα υποχρεωτικά πεδία: {fields}",
  "admin.rewards.field.couponAmount": "Ποσό κουπονιού",
  "admin.rewards.field.couponUnit": "Το ποσό είναι σε",
  "admin.rewards.unit.EUR": "ευρώ (€)",
  "admin.rewards.unit.PERCENT": "ποσοστό (%)",
  "admin.rewards.copyHint": "Και τα δύο φαίνονται σε όλους στην κατάταξη, γι' αυτό συμπλήρωσε κάθε γλώσσα.",
  "admin.rewards.thresholdHint": "Οι πόντοι που πρέπει να φτάσει κάποιος για να την κερδίσει.",
  "admin.rewards.field.couponAmount.placeholder": "π.χ. 10",
  "admin.rewards.field.couponAmount.hint": "Η επιβράβευση δίνεται ως κουπόνι: γράψε πόσο αξίζει, μόνο τον αριθμό, και διάλεξε παρακάτω ευρώ ή ποσοστό. Οι συνάδελφοι δεν βλέπουν αυτόν τον αριθμό — αν θέλεις να τον ξέρουν, γράψ' τον στο «Τι παίρνει».",
  "admin.rewards.field.couponUnit.hint": "Καθορίζει αν το 10 σημαίνει 10 € έκπτωση ή 10% έκπτωση.",
  "admin.rewards.untitled": "Χωρίς όνομα",
  "admin.rewards.incomplete": "Ημιτελής",
  "admin.rewards.off": "Δεν προσφέρεται",
  "rewards.ridesToGo": { one: "1 ακόμη κοινή διαδρομή", other: "Περίπου {n} ακόμη κοινές διαδρομές" },
  "rewards.away": "-{n} ακόμη",
  "rides.empty.driverHintPlain": "Γίνε ο πρώτος — ανέβασε μια διαδρομή για αύριο το πρωί.",
  "admin.rewards.eg": "π.χ.",
  "admin.rewards.labelExample": "Χρυσό κουπόνι",
  "admin.rewards.descriptionExample": "Δωροεπιταγή Box 10 €",

  // ── v2.common ── (keys for shared v2 primitives go directly below)
  "v2.common.seats.option": { one: "1 θέση", other: "{n} θέσεις" },
  "v2.common.seats.taken": { one: "1 θέση είναι ήδη πιασμένη — δεν μπορείς να βάλεις λιγότερες", other: "{n} θέσεις είναι ήδη πιασμένες — δεν μπορείς να βάλεις λιγότερες" },
  "v2.common.seats.tooFew": { one: "Έχεις ήδη 1 επιβάτη, οπότε δεν μπορείς να βάλεις {m}.", other: "Έχεις ήδη {n} επιβάτες, οπότε δεν μπορείς να βάλεις {m}." },
  // Εξηγήσεις «i» (components/v2/ui/InfoTip, views/v2/parts/explain). Απλά λόγια, 1–3 προτάσεις.
  "v2.common.infoTip": "Τι σημαίνει αυτό;",
  "v2.explain.points.title": "Πώς κερδίζεις πόντους",
  "v2.explain.points.rider": { one: "Ως επιβάτης παίρνεις 1 πόντο για κάθε διαδρομή.", other: "Ως επιβάτης παίρνεις {count} πόντους για κάθε διαδρομή." },
  "v2.explain.points.driver": { one: "Ως οδηγός παίρνεις 1 πόντο για κάθε επιβάτη που έχεις μαζί σου.", other: "Ως οδηγός παίρνεις {count} πόντους για κάθε επιβάτη που έχεις μαζί σου." },
  "v2.explain.points.completion": { one: "Και 1 πόντο για κάθε διαδρομή που ολοκληρώνεις ως οδηγός.", other: "Και {count} πόντους για κάθε διαδρομή που ολοκληρώνεις ως οδηγός." },
  "v2.explain.points.generic": "Κερδίζεις πόντους κάθε φορά που ολοκληρώνεται μια κοινή διαδρομή, ως επιβάτης ή ως οδηγός.",
  "v2.explain.points.when": "Οι πόντοι μπαίνουν όταν ο οδηγός πατήσει «Ολοκλήρωση» στο τέλος της διαδρομής. Μαζεύεις πόντους και κερδίζεις επιβραβεύσεις.",
  "v2.explain.streak.title": "Εβδομάδες στη σειρά",
  "v2.explain.streak.body": "Πόσες εβδομάδες συνεχόμενα έκανες τουλάχιστον μία κοινή διαδρομή. Αν περάσει μια ολόκληρη εβδομάδα χωρίς διαδρομή, το μέτρημα ξεκινά από την αρχή.",
  "v2.explain.share.title": "Το μερίδιό σου",
  "v2.explain.share.split": "Τα καύσιμα και τα διόδια της διαδρομής μοιράζονται ίσα σε όλους όσοι είναι στο αυτοκίνητο, μαζί και στον οδηγό.",
  "v2.explain.share.drops": "Όσο περισσότεροι μπαίνουν, τόσο μικραίνει. Το τελικό ποσό βγαίνει όταν ολοκληρωθεί η διαδρομή.",
  "v2.explain.share.pay": "Η εφαρμογή δεν κάνει πληρωμές: το ποσό το κανονίζεις απευθείας με τον οδηγό.",
  "v2.explain.share.driver": "Τόσο πληρώνει κάθε επιβάτης σου. Πληρώνεις κι εσύ το ίδιο μερίδιο, γιατί ταξιδεύετε όλοι μαζί.",
  "v2.explain.share.driverEach": "Τόσο πληρώνει κάθε άτομο στο αυτοκίνητο, κι εσύ μαζί.",
  "v2.explain.share.payDriver": "Η εφαρμογή δεν κάνει πληρωμές· τα κανονίζετε μεταξύ σας.",
  "v2.explain.free.title": "Δωρεάν διαδρομή",
  "v2.explain.free.body": "Ο οδηγός καλύπτει μόνος του τα καύσιμα και τα διόδια, οπότε δεν πληρώνεις τίποτα. Οι πόντοι μετράνε κανονικά.",
  "v2.explain.seats.title": "Θέσεις",
  "v2.explain.seats.body": "Πόσους επιβάτες μπορεί να πάρει ο οδηγός. Μια θέση κλείνει μόνο όταν ο οδηγός δεχτεί το αίτημα κάποιου.",
  "v2.explain.seats.offer": "Πόσους συναδέλφους μπορείς να πάρεις. Εσύ αποφασίζεις ποιους δέχεσαι. Όταν γεμίσουν οι θέσεις, η διαδρομή δεν δέχεται άλλα αιτήματα.",
  "v2.explain.suggest.title": "Πώς διαλέγουμε τις προτάσεις",
  "v2.explain.suggest.home": "Πρώτα βλέπεις τις διαδρομές που ξεκινούν πιο κοντά στο σπίτι σου και φεύγουν πιο σύντομα.",
  "v2.explain.suggest.habits": "Μπορεί να μετρήσουν και οι ώρες που ταξιδεύεις συνήθως και οι οδηγοί με τους οποίους έχεις ξαναπάει.",
  "v2.explain.suggest.noHome": "Δεν έχεις ορίσει διεύθυνση, οπότε βλέπεις πρώτα όσες φεύγουν πιο σύντομα. Όρισέ τη στο Προφίλ για να δεις τις πιο κοντινές.",
  "v2.explain.sort.title": "Πώς ταξινομούνται",
  "v2.explain.sort.nearest": "«{label}»: πρώτα όσες ξεκινούν πιο κοντά σου.",
  "v2.explain.sort.earliest": "«{label}»: με τη σειρά που φεύγουν.",
  "v2.explain.sort.match": "«{label}»: μετράμε μαζί πόσο κοντά ξεκινά και πόσο σύντομα φεύγει, και γράφουμε κάτω από κάθε διαδρομή γιατί σου ταιριάζει.",
  "v2.explain.sort.free": "«{label}»: μόνο διαδρομές όπου ο οδηγός καλύπτει τα έξοδα.",
  "v2.explain.lock.title": "Γιατί έκλεισε η διαδρομή",
  "v2.explain.lock.body": { one: "Μια διαδρομή κλείνει όταν ολοκληρωθεί ή 1 ώρα μετά την αναχώρηση.", other: "Μια διαδρομή κλείνει όταν ολοκληρωθεί ή {count} ώρες μετά την αναχώρηση." },
  "v2.explain.lock.bodyPlain": "Μια διαδρομή κλείνει όταν ολοκληρωθεί ή λίγες ώρες μετά την αναχώρηση.",
  "v2.explain.lock.why": "Από τότε κανείς δεν μπαίνει, δεν φεύγει και δεν αλλάζει κάτι, ώστε το κόστος και οι πόντοι να μένουν σωστοί. Ο οδηγός μπορεί ακόμη να πατήσει «Ολοκλήρωση».",
  "v2.explain.pending.title": "Περιμένεις απάντηση",
  "v2.explain.pending.rider": "Ζήτησες θέση και ο οδηγός δεν έχει απαντήσει ακόμη. Θα πάρεις ειδοποίηση μόλις δεχτεί ή απορρίψει το αίτημα. Μέχρι τότε η θέση δεν είναι δική σου.",
  "v2.explain.pending.driverTitle": "Σου ζητούν θέση",
  "v2.explain.pending.driver": "Κάποιος συνάδελφος ζήτησε θέση στη διαδρομή σου. Εσύ αποφασίζεις: πάτα «Αποδοχή» ή «Απόρριψη» και ειδοποιείται αμέσως.",
  "v2.explain.periods.title": "Περίοδοι κατάταξης",
  "v2.explain.periods.week": { one: "«{label}»: οι πόντοι της τελευταίας ημέρας.", other: "«{label}»: οι πόντοι των τελευταίων {n} ημερών." },
  "v2.explain.periods.month": { one: "«{label}»: οι πόντοι της τελευταίας ημέρας.", other: "«{label}»: οι πόντοι των τελευταίων {n} ημερών." },
  "v2.explain.periods.all": "«{label}»: όλοι οι πόντοι από την αρχή.",
  "v2.explain.periods.place": "Η θέση σου αλλάζει ανάλογα με την περίοδο που διαλέγεις.",
  "v2.explain.reward.title": "Πώς κερδίζεις επιβραβεύσεις",
  "v2.explain.reward.goal": "Έναν στόχο τον κερδίζεις μία φορά, μόλις το σύνολο των πόντων σου φτάσει τον αριθμό του.",
  "v2.explain.reward.recurring": "Τις επιβραβεύσεις «ξανά και ξανά» τις ξανακερδίζεις κάθε φορά που μαζεύεις άλλους τόσους πόντους.",
  "v2.explain.reward.keep": "Οι πόντοι σου δεν ξοδεύονται: μετράει πάντα το σύνολό σου.",
  "v2.explain.impact.title": "Τι μετράμε εδώ",
  "v2.explain.impact.saved": "Εξοικονόμηση: εκτίμηση για όσα γλίτωσες στα έξοδα, περίπου {rider} για κάθε διαδρομή ως επιβάτης και {driver} ως οδηγός.",
  "v2.explain.impact.co2": "CO₂: εκτίμηση για τους ρύπους που δεν βγήκαν, περίπου μισό κιλό για κάθε κοινή διαδρομή.",
  "v2.explain.impact.rides": "Διαδρομές: όσες κοινές διαδρομές ολοκλήρωσες, ως οδηγός ή ως επιβάτης.",
  "v2.explain.admin.title": "Τι βλέπουν οι συνάδελφοι",
  "v2.explain.admin.body": "Ό,τι αποθηκεύεις ισχύει αμέσως για όλους και φαίνεται στην Αρχική και στην Κατάταξη. Οι πόντοι που έχουν ήδη δοθεί δεν αλλάζουν.",
  "v2.explain.confirm.leaveTitle": "Να αποχωρήσεις από τη διαδρομή;",
  "v2.explain.confirm.leaveBody": "Η θέση σου ελευθερώνεται. Ειδοποίηση λαμβάνει: {name}. Για να ξαναμπείς, θα χρειαστεί να ζητήσεις θέση από την αρχή.",
  "v2.explain.confirm.leaveKeep": "Όχι, μένω",
  "v2.explain.confirm.cancelRequestTitle": "Να ακυρώσεις το αίτημά σου;",
  "v2.explain.confirm.cancelRequestBody": "{name} δεν θα βλέπει πια το αίτημά σου. Μπορείς να ζητήσεις θέση ξανά όποτε θέλεις.",
  "v2.explain.confirm.cancelRequestKeep": "Όχι, κράτα το αίτημα",
  "v2.explain.confirm.completeTitle": "Ολοκλήρωση της διαδρομής;",
  "v2.explain.confirm.completeBody": "Πάτα το όταν φτάσετε. Το κόστος μοιράζεται σε όσους ήταν στο αυτοκίνητο και όλοι παίρνουν τους πόντους τους. Αυτό δεν αναιρείται.",
  "v2.explain.confirm.completeKeep": "Όχι ακόμη",
  // Συγκοινωνία ως το σημείο συνάντησης (views/logic/transitWords, TransitLine, AccessCardV2).
  // Οι γραμμές με λέξεις, όχι με κωδικούς: «M3» → «Μετρό Γραμμή 3», «T7» → «Τραμ 7».
  "v2.transit.line.bus": "Λεωφορείο {line}",
  "v2.transit.line.metro": "Μετρό Γραμμή {line}",
  "v2.transit.line.tram": "Τραμ {line}",
  "v2.transit.line.trolley": "Τρόλεϊ {line}",
  "v2.transit.line.train": "Τρένο {line}",
  "v2.transit.line.ferry": "Πλοίο {line}",
  "v2.transit.line.other": "Γραμμή {line}",
  "v2.transit.mode.bus": "Λεωφορείο",
  "v2.transit.mode.metro": "Μετρό",
  "v2.transit.mode.tram": "Τραμ",
  "v2.transit.mode.trolley": "Τρόλεϊ",
  "v2.transit.mode.train": "Τρένο",
  "v2.transit.mode.ferry": "Πλοίο",
  "v2.transit.mode.other": "Συγκοινωνία",
  "v2.transit.by.bus": "με λεωφορείο",
  "v2.transit.by.metro": "με μετρό",
  "v2.transit.by.tram": "με τραμ",
  "v2.transit.by.trolley": "με τρόλεϊ",
  "v2.transit.by.train": "με τρένο",
  "v2.transit.by.ferry": "με πλοίο",
  "v2.transit.by.other": "με συγκοινωνία",
  "v2.transit.by.any": "με συγκοινωνία",
  "v2.transit.board": "Επιβίβαση: {stop}",
  "v2.transit.change": "Αλλαγή: {stop}",
  "v2.transit.alight": "Αποβίβαση: {stop}",
  "v2.transit.step.walkToStop": "Περπάτησε ως τη στάση {stop}",
  "v2.transit.step.walkToVehicle": "Περπάτησε ως την επόμενη στάση",
  "v2.transit.step.walkToPickup": "Περπάτησε ως το σημείο συνάντησης",
  "v2.transit.step.take": "Πάρε {line}",
  "v2.transit.step.takeTowards": "Πάρε {line} προς {headsign}",
  "v2.transit.step.getOff": "Κατέβα στη στάση {stop}",
  "v2.transit.step.getOffAfter": {
    one: "Κατέβα στη στάση {stop}, μετά από 1 στάση",
    other: "Κατέβα στη στάση {stop}, μετά από {n} στάσεις",
  },
  "v2.transit.step.pickupHere": "Εδώ σε παίρνει ο οδηγός: {place}",
  "v2.transit.card.title": "Πώς θα φτάσεις στο σημείο συνάντησης",
  "v2.transit.card.sub": "Από το σπίτι σου ως το σημείο όπου σε παίρνει ο οδηγός. Πάτησε έναν τρόπο για να τον δεις στον χάρτη.",
  "v2.transit.card.subWalkOnly": "Από το σπίτι σου ως το σημείο όπου σε παίρνει ο οδηγός. Ο δρόμος με τα πόδια φαίνεται στον χάρτη.",
  "v2.transit.card.onFoot": "με τα πόδια",
  "v2.transit.card.faster": "{mins} πιο γρήγορα από το περπάτημα",
  "v2.transit.card.noTransit": "Δεν βρήκαμε συγκοινωνία πιο γρήγορη από το περπάτημα.",
  "v2.transit.card.subFar": "Από το σπίτι σου ως το σημείο όπου σε παίρνει ο οδηγός.",
  "v2.transit.card.farNoTransit": "Το σημείο είναι {km} από το σπίτι σου. Είναι πολύ μακριά για περπάτημα και δεν βρήκαμε συγκοινωνία. Διάλεξε διαδρομή που ξεκινά πιο κοντά σου.",
  "v2.transit.card.showSteps": "Δείξε τα βήματα",
  "v2.transit.card.hideSteps": "Κρύψε τα βήματα",
  "v2.transit.card.neitherGood": "Δεν σε βολεύει κανένας τρόπος; Διάλεξε μια διαδρομή που ξεκινά πιο κοντά στο σπίτι σου.",
  "v2.transit.card.tooFar": "Σου πέφτει μακριά; Διάλεξε μια διαδρομή που ξεκινά πιο κοντά στο σπίτι σου.",
  "v2.transit.card.findAnother": "Βρες άλλη διαδρομή",
  "v2.transit.noun.bus": { one: "λεωφορείο", other: "{n} λεωφορεία" },
  "v2.transit.noun.metro": { one: "μετρό", other: "{n} γραμμές μετρό" },
  "v2.transit.noun.tram": { one: "τραμ", other: "{n} γραμμές τραμ" },
  "v2.transit.noun.trolley": { one: "τρόλεϊ", other: "{n} τρόλεϊ" },
  "v2.transit.noun.train": { one: "τρένο", other: "{n} τρένα" },
  "v2.transit.noun.ferry": { one: "πλοίο", other: "{n} πλοία" },
  "v2.transit.noun.other": { one: "συγκοινωνία", other: "{n} γραμμές" },
  "v2.transit.combo.by": "με {modes}",
  "v2.transit.combo.changes": { one: "1 αλλαγή", other: "{n} αλλαγές" },
  "v2.transit.combo.noChange": "χωρίς αλλαγή",
  "v2.transit.combo.label": "{modes}, {changes}",
  "v2.common.dialog.discardQuestion": "Έκανες αλλαγές που δεν αποθηκεύτηκαν. Αν κλείσεις τώρα, θα χαθούν.",
  "v2.common.dialog.keepEditing": "Συνέχισε την επεξεργασία",
  "v2.common.dialog.discard": "Κλείσε χωρίς αποθήκευση",
  "v2.common.map.controls": "Χειρισμός χάρτη",
  "v2.common.map.zoomIn": "Μεγέθυνε τον χάρτη",
  "v2.common.map.zoomOut": "Σμίκρυνε τον χάρτη",
  "v2.common.map.recentre": "Κεντράρισε ξανά τον χάρτη",
  "v2.common.map.keyWalk": "με τα πόδια",
  "v2.common.map.keyTransit": "συγκοινωνία",
  "v2.common.liveRoute": "ζωντανή διαδρομή",
  "v2.common.map.follow": "Ακολούθησέ με",
  "v2.common.map.followOn": "Ο χάρτης σε ακολουθεί — πάτα για να σταματήσει",
  "v2.common.traffic.title": "Κίνηση",
  "v2.common.traffic.free": "ελεύθερη",
  "v2.common.traffic.slow": "αργή",
  "v2.common.traffic.jam": "μποτιλιάρισμα",
  "v2.common.traffic.hintAt": "Τα χρώματα της διαδρομής δείχνουν την κίνηση που περιμένουμε στις {time}.",
  "v2.common.traffic.hintNow": "Τα χρώματα της διαδρομής δείχνουν την κίνηση αυτή τη στιγμή.",
  "v2.common.traffic.past": "Χωρίς χρώματα κίνησης",
  "v2.common.traffic.pastHint": "Η διαδρομή έχει ήδη γίνει, οπότε η γραμμή δεν δείχνει κίνηση.",
  "v2.common.decrease": "Μείωση",
  "v2.common.increase": "Αύξηση",
  "v2.common.required": "υποχρεωτικό",
  "v2.common.podiumEntry": "{n}η θέση: {name}, {points}",
  // ── v2.shell ── (keys for AppShellV2 / PublicLayoutV2 go directly below)
  "v2.shell.points": { one: "{points} πόντος", other: "{points} πόντοι" },
  "v2.shell.pointsWord": { one: "πόντος", other: "πόντοι" },
  "v2.shell.pointsLink": { one: "{points} πόντος · άνοιξε την κατάταξη", other: "{points} πόντοι · άνοιξε την κατάταξη" },
  "v2.shell.streak": { one: "σερί 1 εβδομάδας", other: "σερί {n} εβδομάδων" },
  "v2.shell.accountMenu": "Μενού λογαριασμού: {name}",
  "v2.shell.openProfile": "Άνοιξε το προφίλ σου",
  "v2.shell.navLeaderboard": "Κατάταξη",
  // ── v2.dashboard ── (keys for DashboardV2 go directly below)
  "v2.dashboard.dayYouDrive": "οδηγείς αυτή τη μέρα",
  "v2.dashboard.youDriveThatDay": { one: "Αυτή τη μέρα οδηγείς εσύ (φεύγεις στις {times}). Μπορείς να ζητήσεις θέση μόνο σε διαδρομή που φεύγει πάνω από 1 ώρα πριν ή μετά.", other: "Αυτή τη μέρα οδηγείς εσύ (φεύγεις στις {times}). Μπορείς να ζητήσεις θέση μόνο σε διαδρομή που φεύγει πάνω από {n} ώρες πριν ή μετά." },
  "v2.dashboard.drivingThen": "Οδηγείς τότε",
  "v2.dashboard.drivingThenWhy": "Φεύγεις στις {time} με τη δική σου διαδρομή, οπότε δεν γίνεται να είσαι και επιβάτης τότε.",
  // A seat or a request the reader already holds on the same day, to the same place (the one-seat rule).
  // Shared with the rides list: badge first, then the tooltip that explains it.
  "v2.dashboard.seatThen": "Έχεις θέση τότε",
  "v2.dashboard.seatThenWhy": "Έχεις ήδη θέση στη διαδρομή με οδηγό {name} στις {time}. Κάθε μέρα κρατάς μία θέση για τον ίδιο προορισμό· αν προτιμάς αυτή, άφησε πρώτα την άλλη.",
  "v2.dashboard.requestedThen": "Ζήτησες θέση τότε",
  "v2.dashboard.requestedThenWhy": "Έχεις ήδη ζητήσει θέση στη διαδρομή με οδηγό {name} στις {time}. Κάθε μέρα ζητάς μία θέση για τον ίδιο προορισμό· αν προτιμάς αυτή, ακύρωσε πρώτα εκείνο το αίτημα.",
  // The feed or the reader's own rides failed to load: the title, then humanError's sentence and a retry pill.
  "v2.dashboard.feedFailedTitle": "Οι διαδρομές δεν φόρτωσαν.",
  "v2.dashboard.commitmentsFailedTitle": "Οι διαδρομές σου δεν φόρτωσαν.",
  "v2.dashboard.fromOffice": "Από το γραφείο",
  "v2.dashboard.fromOfficeWhy": "Η διαδρομή ξεκινά από το γραφείο, όπου είσαι ήδη: δεν χρειάζεται να πας κάπου για να μπεις.",
  "v2.dashboard.noAccessDriver": "Οδηγείς εσύ: ξεκινάς από το σημείο αναχώρησης, οπότε δεν χρειάζεται τρόπος να φτάσεις εκεί.",
  "v2.dashboard.noAccessOffice": "Η διαδρομή ξεκινά από το γραφείο, όπου είσαι ήδη.",
  // Η επεξήγηση του κουμπιού «🚶 46 λεπτά · Ⓜ+🚆 35 λεπτά» σε μια πρόταση: {by} = «με τρένο + μετρό».
  "v2.dashboard.accessTipWalk": "Με τα πόδια ως το σημείο συνάντησης: {mins}",
  "v2.dashboard.accessTipTransit": "Ή {by}: {mins}, {changes}",
  // Στη δεύτερη γραμμή μιας πρότασης, μετά τα εικονίδια: «35 λεπτά με μετρό».
  "v2.dashboard.transitShort": "{mins} {by}",
  "v2.dashboard.lockedWhy": "Η διαδρομή έκλεισε, οπότε δεν δέχεται πια αιτήματα.",
  "v2.dashboard.noRidesDayHint": "Δες όλες τις διαδρομές ή διάλεξε άλλη μέρα.",
  "v2.dashboard.noRidesWeekTitle": "Καμία διαδρομή για τις επόμενες μέρες",
  "v2.dashboard.noRidesWeekHint": "Μόλις κάποιος συνάδελφος ανεβάσει διαδρομή, θα τη δεις εδώ. Στις Διαδρομές βρίσκεις και τις μέρες μετά.",
  "v2.dashboard.accessBoth": "Ως το σημείο συνάντησης: με τα πόδια {walk} ή {by} {transit}",
  "v2.dashboard.colleaguesPosted": {
    one: "1 συνάδελφος ανέβασε διαδρομή προς το Μαρούσι",
    other: "{n} συνάδελφοι ανέβασαν διαδρομές προς το Μαρούσι",
  },
  "v2.dashboard.colleaguesPostedShort": { one: "1 συνάδελφος ανέβασε διαδρομή", other: "{n} συνάδελφοι ανέβασαν διαδρομές" },
  "v2.dashboard.moreRides": { one: "+1 ακόμη διαδρομή", other: "+{count} ακόμη διαδρομές" },
  "v2.dashboard.leaves": "φεύγει {when}",
  "v2.dashboard.fastestRoute": "Ταχύτερη διαδρομή",
  "v2.dashboard.onFoot": "με τα πόδια",
  "v2.dashboard.walkShort": "{mins} περπάτημα",
  "v2.dashboard.details": "Λεπτομέρειες",
  "v2.install.title": "Το T-Ride στην οθόνη σου",
  "v2.install.body": "Ανοίγει με ένα άγγιγμα από την οθόνη σου, σε πλήρη οθόνη, σαν κανονική εφαρμογή.",
  "v2.install.install": "Πρόσθεσέ το στην αρχική οθόνη",
  "v2.install.menu1": "Άνοιξε το μενού του browser",
  "v2.install.menu2": "Διάλεξε «Εγκατάσταση» ή «Προσθήκη στην αρχική οθόνη»",
  "v2.install.ios1": "Πάτα Κοινοποίηση στη γραμμή του Safari",
  "v2.install.ios1other": "Άνοιξε το μενού του browser και πάτα Κοινοποίηση",
  "v2.install.ios2": "Διάλεξε «Προσθήκη στην οθόνη Αφετηρίας»",
  "v2.install.close": "Όχι τώρα",
  "v2.install.later": "Θα το βρίσκεις πάντα στο Προφίλ σου.",
  "v2.install.show": "Δες πώς",
  "v2.install.hide": "Κλείσιμο",
  "v2.install.already": "Το T-Ride είναι ήδη στην οθόνη σου — το ανοίγεις από εκεί.",
  "v2.dashboard.map": "Χάρτης",
  "v2.dashboard.callDriver": "Κάλεσε: {name}",
  "v2.dashboard.youDrive": "Οδηγείς εσύ",
  "v2.dashboard.seatsBooked": { one: "{booked} από 1 θέση κλεισμένη", other: "{booked} από {n} θέσεις κλεισμένες" },
  "v2.dashboard.shareShort": "μερίδιο",
  "v2.dashboard.suggestionsTitle": "Έξυπνες προτάσεις για σένα",
  "v2.dashboard.suggestionsShort": "Έξυπνες προτάσεις",
  "v2.dashboard.seeAll": "Δες όλες",
  "v2.dashboard.dayPicker": "Μέρα διαδρομής",
  "v2.dashboard.dayNoRides": "{day} · δεν έχει διαδρομές για να ζητήσεις θέση",
  "v2.dashboard.closest": "πρώτα οι πιο κοντινές",
  "v2.dashboard.soonest": "με σειρά αναχώρησης",
  "v2.dashboard.ridesCount": { one: "1 διαδρομή", other: "{n} διαδρομές" },
  "v2.dashboard.noRidesDay": "Καμία διαδρομή για {day} ακόμη.",
  "v2.dashboard.request": "Ζήτησε θέση",
  "v2.dashboard.requestShort": "Ζήτησε",
  "v2.dashboard.requestFor": "Ζήτησε θέση · οδηγός: {name}",
  "v2.dashboard.join.title": "Να ζητήσεις θέση;",
  "v2.dashboard.join.body": "Θα στείλουμε το αίτημά σου για τη διαδρομή {when}. Οδηγός: {name}.",
  "v2.dashboard.join.pickupHome": "Παραλαβή κοντά στη διεύθυνση του προφίλ σου.",
  "v2.dashboard.join.pickupNone": "Δεν έχεις ορίσει διεύθυνση, οπότε η παραλαβή θα είναι στο σημείο αναχώρησης του οδηγού.",
  "v2.dashboard.join.send": "Στείλε αίτημα",
  "v2.dashboard.locked": "Η διαδρομή έκλεισε — δεν γίνονται πια αλλαγές.",
  "v2.dashboard.lockedBadge": "Έκλεισε",
  "v2.dashboard.points.title": "Οι πόντοι σου",
  "v2.dashboard.streakWeeks": { one: "1 εβδομάδα με διαδρομή", other: "{count} εβδομάδες στη σειρά" },
  "v2.dashboard.rankOverall": "{rank}η θέση από την αρχή",
  "v2.dashboard.ringTo": "ως το {reward}",
  "v2.dashboard.ringLabel": "{percent}% ως το {reward}: έχεις {have} από τους {need} πόντους και σου μένουν {left}",
  "v2.dashboard.nextReward": "Επόμενη επιβράβευση:",
  "v2.dashboard.pointsToGo": { one: "{count} πόντος ακόμη", other: "{count} πόντοι ακόμη" },
  "v2.dashboard.toReward": { one: "1 πόντος ακόμη για {reward}", other: "{count} πόντοι ακόμη για {reward}" },
  "v2.dashboard.rewardProgress": { one: "Έχεις {have} από τους {need} πόντους — σου λείπει 1.", other: "Έχεις {have} από τους {need} πόντους — σου λείπουν {left}." },
  "v2.dashboard.rewardProgressSince": { one: "Από την τελευταία φορά που την κέρδισες έχεις μαζέψει {have} από τους {need} πόντους — σου λείπει 1.", other: "Από την τελευταία φορά που την κέρδισες έχεις μαζέψει {have} από τους {need} πόντους — σου λείπουν {left}." },
  "v2.dashboard.rewardEvery": { one: "Κάθε πόντος που μαζεύεις σού δίνει αυτή την επιβράβευση.", other: "Κάθε φορά που μαζεύεις {count} πόντους, κερδίζεις αυτή την επιβράβευση." },
  "v2.dashboard.rewardOnce": { one: "Την κερδίζεις μία φορά, μόλις μαζέψεις τον πρώτο σου πόντο.", other: "Κερδίζεις αυτή την επιβράβευση μία φορά, όταν φτάσεις τους {count} πόντους συνολικά." },
  "v2.dashboard.stat.saved": "εξοικονόμηση",
  "v2.dashboard.stat.co2": "CO₂ λιγότερο",
  "v2.dashboard.stat.rides": "διαδρομές",
  "v2.dashboard.board.title": "Κατάταξη",
  "v2.dashboard.board.week": "Εβδ.",
  "v2.dashboard.board.month": "Μήνας",
  "v2.dashboard.board.all": "Όλα",
  "v2.dashboard.board.timeframe": "Χρονικό διάστημα κατάταξης",
  "v2.dashboard.board.titleWeek": "Κατάταξη · εβδομάδα",
  "v2.dashboard.board.titleMonth": "Κατάταξη · μήνας",
  "v2.dashboard.board.titleAll": "Κατάταξη · συνολικά",
  "v2.dashboard.board.allShort": "Όλη",
  "v2.dashboard.board.seeAll": "Δες όλη την κατάταξη",
  "v2.dashboard.board.you": "Εσύ · {name}",
  "v2.dashboard.board.empty": "Κανείς δεν κέρδισε πόντους σε αυτή την περίοδο ακόμη.",
  // ── v2.rides ── (keys for RidesV2 and OfferRideV2 go directly below)
  "v2.rides.subtitle": "Βρες συνάδελφο που πάει στη δουλειά την ώρα σου και ζήτα θέση.",
  "v2.rides.offerSubtitle": "Ανέβασε τη διαδρομή σου. Εσύ διαλέγεις ποιους παίρνεις.",
  "v2.rides.transitChip": "{mins} {by}",
  "v2.rides.transitTipTitle": "{mins} {by} ως το σημείο όπου σε παίρνει ο οδηγός",
  "v2.rides.transitTipWalk": "Με τα πόδια θέλεις {mins}.",
  "v2.rides.map.legend": "Υπόμνημα χάρτη",
  "v2.rides.map.rides": "Αφετηρίες οδηγών",
  "v2.rides.map.office": "Γραφείο",
  "v2.rides.map.home": "Το σπίτι σου",
  "v2.rides.map.place": "Το σημείο που διάλεξες",
  "v2.rides.map.walkThere": "Με τα πόδια ως την αφετηρία",
  "v2.rides.map.transitThere": "Με συγκοινωνία ως την αφετηρία",
  "v2.rides.map.wayThere": "Πώς πας ως την αφετηρία",
  "v2.rides.map.byTransit": "{mins} με συγκοινωνία",
  "v2.rides.youDriveThis": "Οδηγείς εσύ",
  "v2.rides.youDriveAt": "Οδηγείς στις {time}",
  "v2.rides.openMyDrive": "Η διαδρομή σου στις {time}",
  "v2.rides.driving.title": "Αυτή τη μέρα οδηγείς εσύ",
  "v2.rides.driving.body": { one: "Σε άλλη διαδρομή μπορείς να ζητήσεις θέση μόνο αν φεύγει τουλάχιστον 1 ώρα πριν ή μετά από τη δική σου.", other: "Σε άλλη διαδρομή μπορείς να ζητήσεις θέση μόνο αν φεύγει τουλάχιστον {n} ώρες πριν ή μετά από τη δική σου." },
  "v2.rides.myDriveAt": "Οδηγείς {time}",
  "v2.rides.offer": "Πρόσφερε",
  "v2.rides.dayFilter": "Ημέρα αναχώρησης",
  "v2.rides.count": { one: "1 διαδρομή", other: "{n} διαδρομές" },
  "v2.rides.requestSeat": "Ζήτησε θέση",
  "v2.rides.requestShort": "Ζήτησε",
  "v2.rides.locked": "Η διαδρομή έκλεισε — δεν γίνονται πια αλλαγές.",
  "v2.rides.lockedShort": "Έκλεισε",
  "v2.rides.confirm.title": "Να ζητήσεις θέση;",
  "v2.rides.confirm.send": "Στείλε το αίτημα",
  "v2.rides.confirm.driverDecides": "Την απόφαση την παίρνει: {name}. Θα λάβεις ειδοποίηση μόλις απαντήσει.",
  // Το σημείο όπου ξεκινά ο οδηγός, όχι το σπίτι σου — όπως το λέει και η σελίδα της διαδρομής.
  "v2.rides.confirm.pickupHome": "Σημείο συνάντησης: {place} — {walk} με τα πόδια από το σπίτι σου",
  // Όταν η συγκοινωνία είναι πιο γρήγορη, όπως τη δείχνει η γραμμή της λίστας: {by} = «με μετρό».
  "v2.rides.confirm.pickupHomeTransit": "Σημείο συνάντησης: {place} — από το σπίτι σου {walk} με τα πόδια ή {transit} {by}",
  "v2.rides.confirm.meetingPoint": "Σημείο συνάντησης: {place}",
  "v2.rides.upcoming.all": "Δες όλες ({n})",
  "v2.rides.upcoming.less": "Δείξε λιγότερες",
  "v2.rides.later.more": { one: "Δες 1 ακόμη", other: "Δες {n} ακόμη" },
  // Διάρκειες ολογράφως (views/v2/parts/duration.js): «1 ώρα 20 λεπτά», όχι «1 ω 20 λ».
  "v2.dur.mins": { one: "1 λεπτό", other: "{n} λεπτά" },
  "v2.dur.hours": { one: "1 ώρα", other: "{n} ώρες" },
  "v2.dur.hoursMins": "{hours} {mins}",
  // Αντίστροφη μέτρηση ως την αναχώρηση: «σε 1 ώρα 20 λεπτά» ({when} από το duration.js).
  "v2.dur.in": "σε {when}",
  "v2.rides.confirm.noHome": "Δεν έχεις ορίσει διεύθυνση στο προφίλ σου, οπότε το αίτημα φεύγει χωρίς σημείο παραλαβής.",
  "v2.rides.failed.title": "Το αίτημα δεν στάλθηκε",
  "v2.rides.offer.routeTitle": "Η διαδρομή σου",
  "v2.rides.offer.mapEmpty": "Διάλεξε σημείο εκκίνησης και η διαδρομή εμφανίζεται εδώ.",
  "v2.rides.offer.cost": "Κόστος",
  "v2.rides.offer.previewEmpty": "Διάλεξε σημείο εκκίνησης για να δεις το κόστος.",
  "v2.rides.offer.fuelUsed.L": "Καύσιμο που θα καταναλώσεις",
  "v2.rides.offer.fuelUsed.kWh": "Ρεύμα που θα καταναλώσεις",
  "v2.rides.offer.fuelRate.L": "{consumption} λίτρα ανά 100 χλμ · {price} το λίτρο",
  "v2.rides.offer.fuelRate.kWh": "{consumption} κιλοβατώρες ανά 100 χλμ · {price} η κιλοβατώρα",
  "v2.rides.offer.startLabel": "Από πού ξεκινάς",
  // Το κουμπί υποβολής όσο λείπει αυτοκίνητο: μία γραμμή και στα 320px.
  "v2.rides.offer.addCarButton": "Πρώτα πρόσθεσε αυτοκίνητο",
  "v2.rides.offer.routeCount": { one: "1 διαδρομή", other: "{n} διαδρομές" },
  "v2.rides.offer.withTraffic": "με την κίνηση στις {time}",
  "v2.rides.offer.departurePast": "Αυτή η ώρα έχει περάσει. Διάλεξε ώρα αναχώρησης αργότερα από τώρα.",
  "v2.rides.offer.returnBeforeDeparture": "Η επιστροφή είναι πριν από την αναχώρηση ({time}). Διάλεξε ώρα επιστροφής μετά από αυτήν.",
  "v2.rides.offer.fixTimeButton": "Πρώτα διόρθωσε την ώρα",
  "v2.rides.offer.addCarHint.L": "Γράψε στο {profile} πόσα λίτρα καίει το αυτοκίνητό σου και πόσο πληρώνεις το λίτρο, και το ποσό βγαίνει μόνο του.",
  "v2.rides.offer.addCarHint.kWh": "Γράψε στο {profile} πόσες κιλοβατώρες καίει το αυτοκίνητό σου και πόσο πληρώνεις την κιλοβατώρα, και το ποσό βγαίνει μόνο του.",
  "v2.rides.offer.addCarHint.any": "Γράψε στο {profile} τι αυτοκίνητο έχεις, πόσο καίει και πόσο πληρώνεις το λίτρο ή την κιλοβατώρα, και το ποσό βγαίνει μόνο του.",
  "v2.rides.searchPlaceholder": "Ψάξε άλλη διεύθυνση",
  "v2.rides.search.noMatch": "Δεν βρήκαμε αυτή τη διεύθυνση. Δοκίμασε οδό και αριθμό, π.χ. «Κηφισίας 99».",
  "v2.rides.pickup.search": "Διεύθυνση εκκίνησης",
  "v2.rides.pickup.change": "Άλλαξε διεύθυνση",
  "v2.rides.seatsFree": { one: "1 θέση ελεύθερη", other: "{n} θέσεις ελεύθερες" },
  "v2.rides.perRider": "ανά επιβάτη",
  "v2.rides.upDriving": { one: "Οδηγείς · {booked} από 1 θέση κλεισμένη", other: "Οδηγείς · {booked} από {n} θέσεις κλεισμένες" },
  "v2.rides.walkTip": "Πόση ώρα περπατάς ως το σημείο όπου σε παίρνει ο οδηγός.",
  "v2.rides.walkFar": "{mins} με τα πόδια (μακριά)",
  "v2.rides.walkFarTip": "Είναι μακριά για περπάτημα. Δες αν σε βολεύει η συγκοινωνία ή ρώτα τον οδηγό αν μπορεί να σε πάρει πιο κοντά.",
  "v2.rides.transit": "{mins} με συγκοινωνία",
  "v2.rides.transitTip": "Με λεωφορείο, τραμ ή μετρό ως το σημείο όπου σε παίρνει ο οδηγός, πιο γρήγορα από το περπάτημα. Γραμμές: {lines}",
  "v2.rides.transitTipPlain": "Με λεωφορείο, τραμ ή μετρό ως το σημείο όπου σε παίρνει ο οδηγός, πιο γρήγορα από το περπάτημα.",
  "v2.rides.days": { one: "1 μέρα", other: "{n} μέρες" },
  "v2.rides.why.distance": "{km} από το σπίτι σου · φεύγει σε {when}",
  "v2.rides.why.leaves": "Φεύγει σε {when}",
  "v2.rides.dayPick": "Μέρα",
  "v2.rides.dayLabel": "{day} · {count}",
  "v2.rides.dayNone": "{day} · καμία διαδρομή",
  "v2.rides.dayTitle.today": "Σήμερα · {day}",
  "v2.rides.dayTitle.tomorrow": "Αύριο · {day}",
  "v2.rides.empty.day": "Δεν υπάρχει ακόμη διαδρομή για {day}.",
  "v2.rides.empty.dayFull": "Όλες οι διαδρομές για {day} γέμισαν.",
  "v2.rides.empty.dayFullHint": "Μπορείς να τις δεις ή να διαλέξεις άλλη μέρα.",
  "v2.rides.empty.dayFree": "Δεν υπάρχει δωρεάν διαδρομή για {day}.",
  "v2.rides.empty.dayFreeHint": "Υπάρχουν διαδρομές με μερίδιο. Δες τις όλες ή διάλεξε άλλη μέρα.",
  "v2.rides.empty.showAll": "Δείξε όλες",
  "v2.rides.empty.showFull": { one: "Δείξε τη γεμάτη", other: "Δείξε τις {n} γεμάτες" },
  "v2.rides.empty.nextDay": "Επόμενη: {day}",
  "v2.rides.onMap": "Δείξε τη διαδρομή στον χάρτη: {name}",
  "v2.rides.transitCombo": "{modes} · {mins}",
  "v2.rides.transitTipChanges": { one: "Κάνεις 1 αλλαγή.", other: "Κάνεις {n} αλλαγές." },
  "v2.rides.transitTipDirect": "Δεν αλλάζεις όχημα.",
  // ── v2.detail ── (keys for RideDetailV2 and PastRidesV2 go directly below)
  "v2.detail.self.label": "Είσαι εδώ — αυτή την κουκκίδα τη βλέπεις μόνο εσύ",
  "v2.detail.self.notNow": "Όχι τώρα",
  "v2.detail.self.lostTitle": "Χάθηκε το σήμα της τοποθεσίας σου",
  "v2.detail.self.private": "Τη θέση σου σε αυτόν τον χάρτη τη βλέπεις μόνο εσύ.",
  "v2.detail.access.choose": "Τρόπος για το σημείο συνάντησης",
  "v2.detail.access.sub": "Από το σπίτι σου ως το σημείο όπου σε παίρνει ο οδηγός. Διάλεξε τρόπο και δες τον στον χάρτη.",
  "v2.detail.access.leaveAt": "Φύγε από το σπίτι στις {time}",
  "v2.detail.access.spare": { one: "Φτάνεις {n} λεπτό πριν έρθει ο οδηγός.", other: "Φτάνεις {n} λεπτά πριν έρθει ο οδηγός." },
  "v2.detail.access.walkDist": "Περπατάς {km} ως το σημείο.",
  "v2.detail.access.leaveTile": "Φύγε από το σπίτι",
  "v2.detail.edit.route.trafficAt": "Οι χρόνοι λογαριάζουν την κίνηση στις {time}.",
  "v2.detail.edit.route.arrive": "άφιξη {time}",
  "v2.detail.edit.route.fastest": "Γρηγορότερη",
  "v2.detail.edit.route.shortest": "Συντομότερη",
  "v2.detail.edit.route.cheapest": "Φθηνότερη",
  "v2.detail.edit.route.each": "περίπου ο καθένας",
  "v2.detail.edit.route.eachFull": { one: "ο καθένας, αν γεμίσει η 1 θέση", other: "ο καθένας, αν γεμίσουν οι {n} θέσεις" },
  "v2.detail.edit.seatShare": { one: "Αν γεμίσει η θέση, ο καθένας πληρώνει περίπου {amount}.", other: "Αν γεμίσουν οι {n} θέσεις, ο καθένας πληρώνει περίπου {amount}." },
  "v2.detail.edit.seatShareFree": "Η διαδρομή είναι δωρεάν — οι επιβάτες δεν πληρώνουν τίποτα.",
  "v2.detail.noBookings": "Κανείς δεν έχει ζητήσει θέση ακόμη. Όταν ζητήσει κάποιος, θα πάρεις ειδοποίηση και θα αποφασίσεις εδώ.",
  "v2.detail.noBookingsClosed": "Κανείς δεν ζήτησε θέση πριν κλείσει η διαδρομή.",
  "v2.detail.noSeatsYet": "Δεν έχει κλείσει θέση κανείς ακόμη.",
  "v2.detail.noSeatsClosed": "Κανείς δεν είχε θέση σε αυτή τη διαδρομή.",
  "v2.detail.seatsTakenOf": "Κλεισμένες θέσεις: {n} από {total}.",
  "v2.detail.closedBadge": "Έκλεισε",
  "v2.detail.nobodyJoined": "Κανείς δεν ήρθε σε αυτή τη διαδρομή.",
  "v2.detail.aloneDriver": "Κανείς δεν έχει έρθει ακόμη, οπότε όλο το κόστος ({total} €) είναι δικό σου. Αν έρθει ένας συνάδελφος, πληρώνετε {each} € ο καθένας.",
  "v2.detail.eyebrowDriverCancelled": "Η διαδρομή που πρόσφερες",
  "v2.detail.cancelledShare": "Η διαδρομή ακυρώθηκε, οπότε κανείς δεν πληρώνει τίποτα.",
  "v2.detail.cancelledDriver": "Ακύρωσες αυτή τη διαδρομή. Όσοι είχαν θέση ειδοποιήθηκαν, και δεν γίνεται πια τίποτα σε αυτήν.",
  "v2.detail.cancelledRider": "Ο οδηγός ακύρωσε αυτή τη διαδρομή, οπότε δεν μπορείς πια να πας με αυτήν.",
  "v2.detail.notFoundBody": "Μπορεί να ακυρώθηκε ή ο σύνδεσμος να μην ισχύει πια. Δες τις διαδρομές που υπάρχουν τώρα.",
  "v2.detail.noBookingsDeparted":"Κανείς δεν ζήτησε θέση πριν φύγεις.",
  "v2.detail.noRidersCancelled":"Η διαδρομή ακυρώθηκε, οπότε δεν έχει επιβάτες.",
  "v2.detail.aloneRider":"Συνολικό κόστος {total} €. Αν μπεις, μοιράζεται στα δύο: {each} € ο καθένας.",
  "v2.detail.freeDriver": "Δωρεάν — τα έξοδα τα καλύπτεις εσύ.",
  "v2.detail.freeRider": "Δωρεάν — τα έξοδα τα καλύπτει ο οδηγός.",
  "v2.detail.costEach": "Κόστος ανά άτομο",
  "v2.detail.completeAlone": "Δεν είχες επιβάτες, οπότε δεν υπάρχει κόστος για μοίρασμα. Αν έκανες τη διαδρομή, πάτα «Ολοκλήρωση» για να μείνει στο ιστορικό σου.",
  "v2.detail.completeAloneBody": "Δεν είχες επιβάτες, οπότε δεν μοιράζεται κόστος. Η διαδρομή μπαίνει στο ιστορικό σου. Αυτό δεν αναιρείται.",
  "v2.detail.tooCloseToEdit": "Δεν γίνονται πια αλλαγές, γιατί πλησιάζει η ώρα αναχώρησης. Αν κάτι άλλαξε, πάρε τηλέφωνο τους επιβάτες σου.",
  "v2.detail.completeLater": "Όταν φτάσετε, πάτα εδώ «Ολοκλήρωση». Έτσι μοιράζεται το κόστος και παίρνετε όλοι πόντους.",
  "v2.detail.completeSoon": "Το κουμπί «Ολοκλήρωση» εμφανίζεται μετά την ώρα αναχώρησης. Πάτα το όταν φτάσετε, για να μοιραστεί το κόστος και να πάρετε όλοι πόντους.",
  "v2.detail.title": "Λεπτομέρειες διαδρομής",
  "v2.detail.eyebrowDriver": "Οδηγείς εσύ",
  "v2.detail.eyebrowRider": "Η θέση σου",
  "v2.detail.eyebrowOther": "Διαδρομή συναδέλφου",
  "v2.detail.arrival": "άφιξη ~{time}",
  "v2.detail.leaves": "φεύγει {when}",
  "v2.detail.route": "Διαδρομή",
  "v2.detail.withTraffic": "με την κίνηση της ώρας αναχώρησης",
  "v2.detail.estimated": "εκτίμηση",
  "v2.detail.seatsTaken": "κλεισμένες θέσεις",
  "v2.detail.requests": "Αιτήματα",
  "v2.detail.aboard": "Στο αυτοκίνητο",
  "v2.detail.removeReturn.action": "Αφαίρεση επιστροφής",
  "v2.detail.removeReturn.title": "Αφαίρεση της επιστροφής;",
  "v2.detail.removeReturn.body": "Η επιστροφή είναι ξεχωριστή διαδρομή. Όσοι έχουν θέση σε αυτήν ειδοποιούνται και θα χρειαστούν άλλον τρόπο να γυρίσουν. Αυτό δεν αναιρείται.",
  "v2.detail.live.title": "Ζωντανή τοποθεσία",
  "v2.detail.live.updated": "Τελευταία ενημέρωση στις {time}",
  "v2.detail.live.idleDriver": "Κοινοποίησε την τοποθεσία σου όταν ξεκινάς, για να βλέπουν οι επιβάτες πού βρίσκεσαι.",
  "v2.detail.eyebrowDriverPast": "Οδήγησες εσύ",
  "v2.detail.eyebrowRiderPast": "Ήσουν επιβάτης",
  "v2.detail.seatsBooked": { one: "{booked} από 1 θέση κλεισμένη", other: "{booked} από {n} θέσεις κλεισμένες" },
  "v2.detail.splitWays": "Συνολικό κόστος {total} €, μοιρασμένο σε {n} άτομα",
  "v2.detail.return.driverTitle": "Η επιστροφή είναι χωριστή διαδρομή",
  "v2.detail.return.driverSub": "Φεύγει {time}",
  "v2.detail.return.riderSub": "Φεύγει {time} — ζήτα θέση χωριστά",
  "v2.detail.clash.pendingTitle": "Έχεις ήδη ζητήσει θέση εκείνη τη μέρα",
  "v2.detail.clash.pendingBody": "Έχεις ήδη ζητήσει θέση στη διαδρομή με οδηγό {name} στις {time}. Αν προτιμάς αυτή, ακύρωσε πρώτα εκείνο το αίτημα.",
  "v2.detail.clash.confirmedBody": "Έχεις ήδη θέση για τις {time}, με οδηγό: {name}. Αν προτιμάς αυτή τη διαδρομή, άφησε πρώτα την άλλη — έτσι η θέση σου εκεί πηγαίνει σε κάποιον συνάδελφο.",
  "v2.detail.clash.drivingTitle": "Έχεις δική σου διαδρομή τότε",
  "v2.detail.clash.drivingBody": "Οδηγείς εκείνη την ώρα (φεύγεις στις {time}). Αν δεν μπορείς να οδηγήσεις, ακύρωσε πρώτα τη δική σου διαδρομή.",
  "v2.detail.clash.openMyDrive": "Δες τη διαδρομή σου",
  "v2.detail.cancelRide.keep": "Όχι, κράτα τη διαδρομή",
  "v2.detail.cancelRide.alsoReturn": "Ακύρωσε και την επιστροφή ({when} · {riders})",
  "v2.detail.cancelRide.noReturnRiders": "χωρίς επιβάτες",
  "v2.detail.cancelRide.alsoReturnHint": "Η επιστροφή είναι ξεχωριστή διαδρομή. Αν το αφήσεις κλειστό, η επιστροφή μένει όπως είναι.",
  "v2.detail.removeReturn.keep": "Όχι, κράτα την επιστροφή",
  "v2.pastRides.subtitle": "Οι διαδρομές που ολοκλήρωσες, ως οδηγός ή ως επιβάτης, και οι πόντοι που σου έδωσαν.",
  "v2.pastRides.role.all": "Όλες",
  "v2.pastRides.role.drove": "Ως οδηγός",
  "v2.pastRides.role.rode": "Ως επιβάτης",
  "v2.pastRides.badge.rode": "ΗΜΟΥΝ ΕΠΙΒΑΤΗΣ",
  "v2.pastRides.pointsUnit": { one: "πόντος", other: "πόντοι" },
  "v2.pastRides.stat.points": "Πόντοι που κέρδισες",
  "v2.pastRides.pointsPast": "Οι παλιές διαδρομές κρατούν τους πόντους που ίσχυαν τότε.",
  "v2.pastRides.office": "Γραφείο",
  "v2.pastRides.pointsFailed": "Δεν μπορέσαμε να φορτώσουμε τους πόντους σου αυτή τη στιγμή. Οι διαδρομές είναι όλες εδώ· ανανέωσε τη σελίδα σε λίγο για να δεις και τους πόντους.",
  "v2.pastRides.period.7": "Οι διαδρομές σου τις τελευταίες 7 ημέρες",
  "v2.pastRides.period.30": "Οι διαδρομές σου τις τελευταίες 30 ημέρες",
  "v2.pastRides.period.all": "Όλες οι διαδρομές σου",
  "v2.pastRides.empty.hint": "Δοκίμασε «Συνολικά» και «Όλες», ή δείξε όλες με ένα πάτημα.",
  "v2.pastRides.empty.reset": "Δείξε όλες",
  "v2.pastRides.none.title": "Δεν έχεις κάνει ακόμη καμία διαδρομή",
  "v2.pastRides.none.hint": "Μόλις ολοκληρωθεί η πρώτη σου διαδρομή, ως οδηγός ή ως επιβάτης, θα τη βρεις εδώ.",
  // ── v2.leaders ── (keys for LeaderboardV2 and NotificationsV2 go directly below)
  "v2.notif.completedWith": { one: "Ολοκλήρωσες τη διαδρομή με 1 επιβάτη", other: "Ολοκλήρωσες τη διαδρομή με {n} επιβάτες" },
  "v2.leaders.subtitle": "Δες ποιοι συνάδελφοι μάζεψαν τους περισσότερους πόντους και πόσο απέχεις από την επόμενη επιβράβευση.",
  "v2.leaders.emptyAction": "Βρες διαδρομή",
  "v2.notif.subtitle": "Εδώ βλέπεις ποιος ζήτησε θέση, τι απάντησαν οι οδηγοί και τι άλλαξε στις διαδρομές σου.",
  "v2.notif.emptyHint": "Όταν ζητήσεις θέση ή σου ζητήσουν, η απάντηση θα έρθει εδώ.",
  // The feed could not be loaded: what to do about it, under notif.loadFailed.
  "v2.notif.loadFailedHint": "Έλεγξε ότι έχεις σύνδεση στο διαδίκτυο και πάτα «Δοκίμασε ξανά». Αν δεν πετύχει, ξαναδοκίμασε σε λίγα λεπτά.",
  "v2.leaders.timeframe": "Περίοδος κατάταξης",
  "v2.leaders.yourPosition": "Η θέση σου",
  "v2.leaders.offBoard": "Η κατάταξη δείχνει λιγότερες θέσεις από τη δική σου — η θέση σου μετράει κανονικά.",
  "v2.leaders.you": "Εσύ",
  "v2.leaders.youRow": "Εσύ · {name}",
  "v2.leaders.rest": "Υπόλοιπη κατάταξη",
  "v2.leaders.nextReward": "Θα πάρεις πρώτα:",
  "v2.leaders.unread": "Νέα",
  "v2.leaders.topSpot.week": "Είσαι #1 αυτή την εβδομάδα — κράτα τη θέση σου.",
  "v2.leaders.topSpot.month": "Είσαι #1 αυτόν τον μήνα.",
  "v2.leaders.topSpot.all": "Είσαι #1 συνολικά.",
  "v2.leaders.overtake": { one: "Σου λείπει {points} για την επόμενη θέση ({name}) — {rides}.", other: "Σου λείπουν {points} για την επόμενη θέση ({name}) — {rides}." },
  "v2.leaders.overtakeRides": { one: "περίπου μία κοινή διαδρομή", other: "περίπου {count} κοινές διαδρομές" },
  "v2.leaders.podiumPoints": { one: "{points} πόντος", other: "{points} πόντοι" },
  // Both placeholders are consumed by a split in LeaderboardV2; {points} is v2.leaders.behindPoints.
  "v2.leaders.behind": { one: "Είσαι {points} μπροστά από τη θέση πίσω σου ({name}).", other: "Είσαι {points} μπροστά από τη θέση πίσω σου ({name})." },
  "v2.leaders.behindPoints": { one: "{points} πόντο", other: "{points} πόντους" },
  "v2.notif.groupCount": { one: "1 ειδοποίηση", other: "{n} ειδοποιήσεις" },
  "v2.notif.freeForRiders": "Δωρεάν για τους επιβάτες",
  "v2.leaders.notif.closed": "ΕΚΛΕΙΣΕ",
  // A join request once it is no longer waiting: said in the past, so it never reads as a new ask.
  "v2.leaders.notif.requestAccepted": "Δέχτηκες στη διαδρομή σου: {name}",
  "v2.leaders.notif.requestDeclined": "Δεν δέχτηκες στη διαδρομή σου: {name}",
  "v2.leaders.notif.requestAsked": "{name} ζήτησε να έρθει μαζί σου",
  // Χωρίς «τον/την»: το όνομα μπαίνει στο τέλος, μετά από άνω-κάτω τελεία.
  "v2.leaders.notif.bookingCompleted": "Ολοκληρώθηκε η διαδρομή σου με οδηγό: {name}",
  "v2.leaders.notif.rideCompleted": "Ολοκληρώθηκε η διαδρομή σου με επιβάτη: {name}",
  // A departure reminder viewed after the ride's departure time.
  "v2.leaders.notif.reminderPast": "Σου θυμίσαμε ότι η διαδρομή σου θα έφευγε σύντομα",
  "v2.leaders.notif.reminderBadge": "ΥΠΕΝΘΥΜΙΣΗ",
  "v2.leaders.rewards.title": "Πόντοι και επιβραβεύσεις",
  "v2.leaders.rewards.nextGoal": "Ο επόμενος στόχος σου",
  "v2.leaders.rewards.progress": { one: "{have} / {target} πόντος", other: "{have} / {target} πόντοι" },
  "v2.leaders.rewards.toGo": { one: "{points} πόντος ακόμη", other: "{points} πόντοι ακόμη" },
  "v2.leaders.rewards.inPoints": { one: "σε {points} πόντο", other: "σε {points} πόντους" },
  "v2.leaders.rewards.goalsReached": { one: "1 στόχος που πέτυχες", other: "{n} στόχοι που πέτυχες" },
  "v2.leaders.rewards.allCollected": { one: "1 επιβράβευση που πήρες όσες φορές επιτρέπεται", other: "{n} επιβραβεύσεις που πήρες όσες φορές επιτρέπεται" },
  "v2.leaders.rewards.earnedTimes": { one: "κέρδισες 1 φορά μέχρι τώρα", other: "κέρδισες {n} φορές μέχρι τώρα" },
  "v2.leaders.rewards.show": "Δείξε",
  "v2.leaders.rewards.hide": "Κρύψε",
  "v2.leaders.rewards.all": "Όλες οι επιβραβεύσεις",
  "v2.leaders.rewards.fewer": "Κρύψε τις επιβραβεύσεις",
  "v2.leaders.rewards.laterGoals": "Επόμενοι στόχοι",
  "v2.leaders.rewards.showAllGoals": "Δείξε όλους ({n})",
  "v2.leaders.rewards.showFewerGoals": "Δείξε λιγότερους",
  "v2.leaders.rewards.every": { one: "κάθε {points} πόντο", other: "κάθε {points} πόντους" },
  "v2.leaders.rewards.cap": { one: "μόνο μία φορά", other: "έως {n} φορές" },
  // Rewards card sections fold under their eyebrow; a folded section keeps a one-line summary.
  "v2.leaders.rewards.closeAll": "Κλείσε όλα",
  "v2.leaders.rewards.openAll": "Άνοιξε όλα",
  "v2.leaders.rewards.earnedTitle": "Στόχοι που πέτυχες",
  "v2.leaders.rewards.goalsCount": { one: "1 στόχος", other: "{n} στόχοι" },
  "v2.leaders.rewards.rewardsCount": { one: "1 επιβράβευση", other: "{n} επιβραβεύσεις" },
  "v2.leaders.rewards.nearestGoal": { one: "ο πιο κοντινός σε {points} πόντο", other: "ο πιο κοντινός σε {points} πόντους" },
  "v2.leaders.rewards.nearestReward": { one: "η πιο κοντινή σε {points} πόντο", other: "η πιο κοντινή σε {points} πόντους" },
  "v2.leaders.rewards.allTaken": "τις πήρες όλες",
  "v2.leaders.rewards.times": { one: "κέρδισες 1 φορά", other: "κέρδισες {n} φορές" },
  "v2.leaders.rewards.timesOf": { one: "κέρδισες {got} από {n} φορά", other: "κέρδισες {got} από {n} φορές" },
  // ── v2.profile ── (keys for ProfileV2, PointsSystemV2, NotFoundV2 go directly below)
  "v2.profile.subtitle": "Όρισε από πού ξεκινάς, το τηλέφωνο και το αυτοκίνητό σου, και πώς θέλεις να σε ειδοποιούμε.",
  "v2.profile.delete.passwordHint": "Γράψε τον κωδικό σου και μετά πάτα «{action}».",
  "v2.profile.pickupTitle": "Σημείο επιβίβασης",
  // Shown on the interface-version card right after the switch, so the admin sees it took.
  "v2.profile.uiVersion.switched": "Όλοι βλέπουν τώρα την {version}.",
  "v2.profile.uiVersion.confirmTitle": "Να αλλάξεις την εμφάνιση για όλους;",
  "v2.profile.uiVersion.confirmBody": "Όλοι οι συνάδελφοι θα δουν την {version} μόλις ανοίξουν ή ανανεώσουν την εφαρμογή, και η σελίδα σύνδεσης αλλάζει κι αυτή. Μπορείς να την αλλάξεις ξανά από εδώ όποτε θέλεις.",
  "v2.profile.uiVersion.confirmKeep": "Όχι, κράτα την {version}",
  "v2.profile.uiVersion.confirmAction": "Άλλαξε στην {version}",
  "v2.profile.seeAll": "Δες όλες",
  "v2.profile.backHome": "Πίσω στην αρχική",
  "v2.profile.points.unsaved": "Έχεις αλλαγές που δεν αποθηκεύτηκαν.",
  "v2.profile.points.adminOnly": "Το σύστημα πόντων το αλλάζουν μόνο οι διαχειριστές.",
  "v2.profile.points.collapseAll": "Σύμπτυξη όλων",
  "v2.profile.points.expandAll": "Ανάπτυξη όλων",
  "v2.profile.points.toggleSection": "Άνοιξε ή κλείσε: {title}",
  "v2.profile.points.unsavedShort": "Μη αποθηκευμένες αλλαγές",
  "v2.profile.points.discardShort": "Απόρριψη",
  "v2.profile.points.showAll": "Δείξε όλες ({n})",
  "v2.profile.points.showFewer": "Δείξε λιγότερες",
  // The recurring card's add pill; its spoken name is the full "admin.recurring.add".
  "v2.profile.points.addRecurring": "Πρόσθεσε επιβράβευση",
  // The closing bar's short form of "admin.err.missing"; the fields themselves are ringed in red.
  "v2.profile.points.fixRequired": "Λείπουν υποχρεωτικά πεδία. Συμπλήρωσε όσα έχουν κόκκινο πλαίσιο και πάτα ξανά «Αποθήκευση».",
  // Profile: a refused driver switch or ride-email choice, which the shared hook put back without a word.
  "v2.profile.driver.saveFailed": "Η αλλαγή δεν αποθηκεύτηκε. Έλεγξε τη σύνδεσή σου και πάτα ξανά τον διακόπτη.",
  "v2.profile.rideEmails.saveFailed": "Η επιλογή σου για τα email δεν αποθηκεύτηκε. Έλεγξε τη σύνδεσή σου και διάλεξε ξανά.",
  "v2.profile.points.nothingToSave": "Δεν υπάρχουν αλλαγές για αποθήκευση.",
  // What the badge beside a list's title counts, for a screen reader and a hover.
  "v2.profile.points.count": { one: "1 επιβράβευση", other: "{n} επιβραβεύσεις" },
  // A folded section still says what it is set to.
  "v2.profile.points.summary.points": "Επιβάτης {rider} · οδηγός {perRider} ανά επιβάτη και {completion} ανά διαδρομή",
  "v2.profile.points.summary.ranking": "Εβδομάδα {week} μέρες · μήνας {month} μέρες · {size} θέσεις στη λίστα",
  // Each reward row's controls, named with the reward, so sixteen "Αφαίρεση" can be told apart.
  "v2.profile.points.rowOffered": "Προσφέρεται: {name}",
  "v2.profile.points.rowEdit": "Επεξεργασία: {name}",
  "v2.profile.points.rowClose": "Κλείσιμο: {name}",
  "v2.profile.points.rowRemove": "Αφαίρεση: {name}",
  "v2.profile.points.copyHint": "Το όνομα και το «Τι παίρνει» φαίνονται σε όλους στην Κατάταξη — γράψ' τα και στα ελληνικά και στα αγγλικά.",
  "v2.profile.impact.failed": "Δεν μπορέσαμε να φορτώσουμε τα σύνολά σου. Ανανέωσε τη σελίδα για να ξαναδοκιμάσεις.",
  "v2.profile.uiVersion.hint": "Ισχύει για όλους τους συναδέλφους και για τη σελίδα σύνδεσης. Τη βλέπουν μόλις ανοίξουν ή ανανεώσουν την εφαρμογή.",
  "v2.profile.driver.hint":
    "Μπορείς πάντα να μπαίνεις σε διαδρομές ως επιβάτης. Αν το ανοίξεις, εμφανίζονται από κάτω οι ερωτήσεις για το αυτοκίνητό σου, από τις οποίες υπολογίζεται πόσο πληρώνει ο καθένας για τη διαδρομή.",
  "v2.profile.car.hint": "Από εδώ υπολογίζεται το μερίδιο του καθενός στο κόστος — και το δικό σου.",
  // Short enough to stay whole in a ~65px tile on a 320px phone; a verb, so the figure reads as a sentence.
  "v2.profile.impact.saved": "Γλίτωσες",
  "v2.profile.impact.co2": "Λιγότερο CO₂",
  "v2.profile.impact.rides": "Διαδρομές",
  "v2.profile.fuel.COMBUSTION": "Καύσιμο",
  "v2.profile.fuel.ELECTRIC": "Ρεύμα",
  "v2.profile.fuel.covers": "«Καύσιμο» σημαίνει βενζίνη, πετρέλαιο, υγραέριο ή υβριδικό που δεν μπαίνει στην πρίζα. «Ρεύμα» σημαίνει ηλεκτρικό.",
  "v2.profile.streakChip": { one: "1 εβδομάδα στη σειρά", other: "{n} εβδομάδες στη σειρά" },
  "v2.profile.streakFull": { one: "1 εβδομάδα με κοινή διαδρομή", other: "{n} εβδομάδες στη σειρά με κοινή διαδρομή" },
  // Profile photo (identity card, framing dialog, removal).
  "v2.profile.photo.add": "Πρόσθεσε φωτογραφία",
  "v2.profile.photo.change": "Άλλαξε φωτογραφία",
  "v2.profile.photo.remove": "Αφαίρεση",
  "v2.profile.photo.removeLong": "Αφαίρεση φωτογραφίας",
  "v2.profile.photo.cameraLabel": "Άλλαξε τη φωτογραφία σου",
  "v2.profile.photo.tipLabel": "Ποιος βλέπει τη φωτογραφία σου;",
  "v2.profile.photo.title": "Η φωτογραφία σου",
  "v2.profile.photo.explain": "Τη βλέπουν μόνο οι συνάδελφοι που έχουν συνδεθεί. Αφαιρούμε από τη φωτογραφία τις πληροφορίες τοποθεσίας.",
  "v2.profile.photo.formats": "Διάλεξε μια φωτογραφία από το κινητό ή τον υπολογιστή σου (JPG ή PNG, έως {mb} MB).",
  "v2.profile.danger.title": "Διαγραφή λογαριασμού",
  "v2.profile.pickup.clearSearch": "Σβήσε την αναζήτηση",
  "v2.profile.pickup.searchPlaceholder": "Γράψε μια διεύθυνση",
  "v2.profile.photo.dragHint": "Σύρε τη φωτογραφία και άλλαξε το ζουμ, ώστε να φαίνεσαι μέσα στον κύκλο.",
  "v2.profile.photo.cropLabel": "Η φωτογραφία σου. Τα βέλη τη μετακινούν, τα + και − αλλάζουν το ζουμ.",
  "v2.profile.photo.zoom": "Ζουμ",
  "v2.profile.photo.zoomIn": "Μεγάλωσε",
  "v2.profile.photo.zoomOut": "Μίκρυνε",
  "v2.profile.photo.progressLabel": "Πρόοδος αποστολής",
  "v2.profile.photo.uploading": "Στέλνουμε τη φωτογραφία… {pct}%",
  "v2.profile.photo.finishing": "Σχεδόν έτοιμο…",
  "v2.profile.photo.saved": "Η φωτογραφία σου αποθηκεύτηκε.",
  "v2.profile.photo.removed": "Η φωτογραφία σου αφαιρέθηκε.",
  "v2.profile.photo.removeTitle": "Να αφαιρεθεί η φωτογραφία;",
  "v2.profile.photo.removeBody": "Θα φαίνονται τα αρχικά σου.",
  "v2.profile.photo.dropHere": "Άφησε τη φωτογραφία εδώ",
  // ── v2.public ── (keys for LoginV2, ForgotPasswordV2, 404 go directly below)
  "v2.public.forgot": "Ξέχασες τον κωδικό;",
  "v2.public.stepEmail": "Email",
  "v2.public.stepCode": "Κωδικός",
  "v2.public.codeIncomplete": "Γράψε και τα 6 ψηφία για να συνεχίσεις.",
  "v2.public.demoTitle": "ΛΟΓΑΡΙΑΣΜΟΙ ΔΟΚΙΜΗΣ",
  "v2.public.stepOf": "Βήμα {n} από {total}",
  "v2.public.strength": "Ισχύς κωδικού: {label}",
  "v2.public.resent": "Σου στείλαμε νέο κωδικό. Ο προηγούμενος δεν ισχύει πια.",
  "v2.public.welcomeTitle": "Καλώς ήρθες στο T-Ride",
  "v2.public.welcomeHint": "Συνδέσου με το εταιρικό σου email για να μοιραστείς τη διαδρομή σου προς το Μαρούσι.",
  "v2.public.blurb": "Για την ώρα το T-Ride λειτουργεί δοκιμαστικά. Δεν χρειάζεται εγγραφή: τον λογαριασμό σου τον ανοίγει ο διαχειριστής της εφαρμογής.",
  "v2.public.asideTitle": "Πήγαινε στο γραφείο μαζί με συναδέλφους",
  "v2.public.reset.asideEyebrow": "ΝΕΟΣ ΚΩΔΙΚΟΣ",
  "v2.public.reset.asideTitle": "Φτιάξε νέο κωδικό σε τρία βήματα",
  "v2.public.reset.aside1": "Γράψε το εταιρικό σου email.",
  "v2.public.reset.aside2": "Βάλε τον 6ψήφιο κωδικό που θα σου στείλουμε στο email.",
  "v2.public.reset.aside3": "Διάλεξε νέο κωδικό και συνδέσου ξανά.",
  "v2.public.reset.asideFooter": "Δεν βρίσκεις το email; Κοίτα και στον φάκελο ανεπιθύμητων ή ζήτησε νέο κωδικό.",
  "v2.public.err.emailEmpty": "Γράψε το εταιρικό σου email.",
  "v2.public.err.emailFormat": "Αυτό δεν μοιάζει με email. Γράψε το ολόκληρο, π.χ. onoma@ote.gr.",
  "v2.public.err.passwordEmpty": "Γράψε τον κωδικό σου.",
  "v2.public.pointsEyebrow": "ΓΙΑΤΙ T-RIDE",
  "v2.public.point.1": "Βρες συναδέλφους που κάνουν την ίδια διαδρομή με σένα και πηγαίνετε μαζί στη δουλειά.",
  "v2.public.point.2": "Κάθε διαδρομή που μοιράζεσαι σού δίνει πόντους, και οι πόντοι φέρνουν ανταμοιβές.",
  "v2.public.point.3": "Λιγότερα αυτοκίνητα στον δρόμο σημαίνει λιγότερη κίνηση και λιγότερους ρύπους στο Μαρούσι.",
};
